import type { UploadBatchDeclaration, UploadBatchReceipt, UploadBatchListResponse } from './types';
import createClient, { type Middleware } from 'openapi-fetch';
import type { ExtractionRunApiClient } from './extraction-run-client';
import type { components, paths } from './generated/schema';
import { REQUEST_BODY_MAX_BYTES, bulkCellMutationsBodyBytes } from './request-body-limit';
import { parseRetryAfterSeconds, type RetryAfterSeconds } from './retry-after';
import type { AuthorizedUploadResponse, UploadSessionCreateRequest, UploadSessionResponse } from './types';
import type {
  AcceptTermsRequest,
  AnalysisExportDocumentFilterRequest,
  AnalysisFieldReviewSummaryResponse,
  AnalysisReviewQueueResponse,
  ConfidenceTier,
  AccessRequestAcceptedResponse,
  AccessRequestResponse,
  AiFeedbackUpsertRequest,
  AnalysisPresenceHeartbeatRequest,
  AnalysisPresenceListResponse,
  AnalysisPresenceSnapshotResponse,
  AnalysisLocksResponse,
  BulkProjectPresenceResponse,
  AnalysisGridResponse,
  AnalysisFreshnessChangesResponse,
  AnalysisGridMetadataResponse,
  AnalysisGridRowSnapshotResponse,
  AnalysisGridRowsPageResponse,
  AnalysisCellDetailResponse,
  AnalysisExportListResponse,
  AnalysisExportResponse,
  AnalysisExtractionResponse,
  AnalysisExtractionPreviewResponse,
  AnalysisExtractionStatusResponse,
  AnalysisResponse,
  AnalysisRevisionResponse,
  AddTsaColumnRequest,
  AddTsaServiceRequest,
  AuthBootstrapResponse,
  AssignMemberRequest,
  AuthSessionResponse,
  BatchProcessingLogPageResponse,
  BatchListItemResponse,
  BatchSubmissionReceiptResponse,
  SubmitBatchRequest,
  BatchStatusSnapshotResponse,
  BillingGraphPointResponse,
  BillingPeriodDetailResponse,
  BillingPeriodSummaryResponse,
  BillingSummaryResponse,
  BillingWorkspaceCostBreakdownResponse,
  BillingWorkspaceDirectorySummaryResponse,
  ProviderCostReconciliationRequest,
  ProviderCostReconciliationResponse,
  ProviderCostCorrectionListResponse,
  UnresolvedProviderCostEventListResponse,
  BulkCellMutationsRequest,
  BulkCellMutationsResponse,
  BulkCellMutationItemRequest,
  BulkMoveDocumentsRequest,
  BulkMoveDocumentsResponse,
  CellConcurrencyConflictResponse,
  CellLockConflictResponse,
  CellLockReleaseRequest,
  CellLockRequest,
  CellLockResponse,
  CellReviewRequest,
  ChargeCodeResponse,
  ChargeCodeSuggestionResponse,
  CliLoginApprovalRequest,
  CliLoginApprovalResponse,
  CliLoginDenialResponse,
  ControlPlanePortfolioQuery,
  ControlPlaneSnapshotResponse,
  SetTenantAccountKindRequest,
  WriteCellOverrideRequest,
  CreateAnalysisExportRequest,
  CreateAnalysisRequest,
  CreateChargeCodeRequest,
  CreateProjectAutomationRuleRequest,
  CreateRevisionRequest,
  CreateExtractionRequest,
  CurrentAiFeedbackResponse,
  DashboardActivityEnvelopeResponse,
  DashboardProjectListResponse,
  DashboardVisitResponse,
  DocumentPreviewResponse,
  UploadBatchInputOutcome,
  DocumentUploadOutcomesResponse,
  CreateUserRequest,
  DocumentDetailResponse,
  DocumentFolderCreateRequest,
  DocumentFolderRenameRequest,
  DocumentFolderResponse,
  DocumentTagOptionsResponse,
  DocumentFolderTreeResponse,
  DocumentFolderUpdateRequest,
  DocumentTagsUpdateRequest,
  ErrorResponse,
  GridColumnResponse,
  EvaluationRunDetailResponse,
  EvaluationRunLookupResponse,
  EvaluationRunResponse,
  EvaluationRunStartRequest,
  FolioActionExecutionRequest,
  FolioActionExecutionResponse,
  FolioChatRequest,
  FolioConversationDetailResponse,
  FolioConversationResponse,
  FolioGuidanceResponse,
  GroundTruthSampleCreateRequest,
  GroundTruthSampleResponse,
  HealthResponse,
  OutcomeSuggestionsResponse,
  InviteTenantUserRequest,
  MyAccessRequestStatusResponse,
  PatchChargeCodeRequest,
  PatchTenantRequest,
  PatchUserRequest,
  PlatformInvitationResponse,
  PermissionCatalogResponse,
  ProjectAutomationRuleResponse,
  ProjectMemberResponse,
  ProjectMembersResponse,
  AssignableProjectUsersResponse,
  ProjectCapabilitiesPatchRequest,
  ProjectPermissionsResponse,
  ProjectCostTotalResponse,
  ProjectPresenceHeartbeatRequest,
  ProjectPresenceSnapshotResponse,
  ProjectResponse,
  ProjectTrashResponse,
  ProjectUpsertRequest,
  RejectAccessRequestRequest,
  ReleaseNewsResponse,
  AgentConnectionResponse,
  ResolveCanonicalAnalysisRequest,
  ResolveCanonicalAnalysisResponse,
  ReviewChargeCodeRequest,
  RunListItemResponse,
  RunOverviewResponse,
  RunResultDetailResponse,
  RunResultsResponse,
  SessionBootstrapResponse,
  PaginatedStoredTextDocumentSummaryResponse,
  ProjectDocumentIngestSummaryResponse,
  StoredTextDocumentSummaryResponse,
  SubmitJoinAccessRequest,
  SubmitNewTenantAccessRequest,
  SwitchTenantRequest,
  TenantDetailResponse,
  TenantOnboardingRequest,
  TenantOnboardingResponse,
  TenantResponse,
  TenantTrialRequest,
  TenantUsageByChargeCodeResponse,
  TenantUsageSummaryResponse,
  WorkspaceInvitationResponse,
  WorkspaceInvitationStatus,
  TemplateAssignRequest,
  TemplateCreateRequest,
  TemplateDetailResponse,
  TemplatePreviewRequest,
  TemplatePreviewResponse,
  TemplateReviewRequest,
  TemplateReviewResponse,
  TemplateResponse,
  TemplateSummaryResponse,
  TemplateValidationRequest,
  TemplateValidationResponse,
  AuthorableTemplateType,
  TemplateVersionCreateRequest,
  TemplateVersionResponse,
  TemplateWorkbookImportResponse,
  TsaExportFormat,
  TsaFieldCitationResponse,
  TsaFieldValueResponse,
  TsaGenerationRunDetailApiResponse,
  TsaGenerationRunDetailResponse,
  TsaGenerationRunsResponse,
  TsaGenerationStatusResponse,
  TsaRiskAssessmentResponse,
  TsaFactSheetResponse,
  TsaFindingsResponse,
  TsaCostsResponse,
  TsaSectionItemResponse,
  TsaSectionResponse,
  TsaServiceExtractionResponse,
  TsaIdentifiedServicesResponse,
  TsaCapabilityRunsResponse,
  StartTsaGenerationRequest,
  RegenerateTsaGenerationRequest,
  UpdateTsaServiceFieldRequest,
  UpdateMemberPermissionsRequest,
  UpdateMembershipPermissionsRequest,
  UpdateProjectAutomationRuleRequest,
  UsageSummaryResponse,
  ApiKeyUsageResponse,
  CreateUserApiKeyRequest,
  CreateUserApiKeyResponse,
  UserApiKeyProvisioningContextResponse,
  UserApiKeyResponse,
  UserProjectAccessResponse,
  UserResponse,
  WorkspaceMemberResponse,
  WorkspaceCatalogResponse,
  WorkspacePricingRequest,
  WorkspacePricingResponse,
  MarketplaceCatalogResponse,
  ManualChargeRequest,
  ManualChargeResponse,
  ManualChargeListResponse,
  WorkspaceInvitationListResponse,
  PlatformInvitationListResponse,
  MeResponse,
  WorkspaceTrashResponse,
} from './types';

/*
 * Public contract types are exported by the package entry point directly
 * from `types.ts`. The client facade imports them for method signatures but
 * deliberately does not re-export them. This keeps one public ownership path
 * for generated contracts and prevents the facade from becoming a second
 * contract barrel alongside the package entry point.
 */

export const DEFAULT_API_BASE_URL = '/api/v1';

// The API answers a bulk cell body over the request-body limit with this error, so the
// client refuses such a request before sending it.
const BULK_CELL_MUTATIONS_TOO_LARGE_CODE = 'bulk-cell-mutations-too-large';
const BULK_CELL_MUTATIONS_TOO_LARGE_MESSAGE =
  'These changes are too large to apply in one step. Select fewer cells and try again.';

// Sentinel absolute base URL handed to openapi-fetch so its internal `new Request(...)` always
// receives an absolute URL (relative URLs throw under Node/undici). The custom fetch below
// strips this sentinel (and the `/api/v1` routing prefix) before resolving the real URL.
const INTERNAL_BASE_URL = 'http://openapi-fetch.invalid';
const ROUTING_PREFIX = '/api/v1';
const DEFAULT_BATCH_SUBMISSION_TIMEOUT_MS = 300_000;
/** Declared on every secured response; see `components.headers` in the contract. */
const SESSION_DEADLINE_HEADER = 'X-CA-Session-Expires-At';
/** Marks a request the user caused; only those slide the session's idle window. */
const USER_ACTIVITY_HEADER = 'X-CA-User-Active';

export interface ContractAnalyzerAppApiClientOptions {
  baseUrl?: string;
  credentials?: RequestCredentials;
  fetch?: typeof fetch;
  headers?: HeadersInit;
  onAuthFailure?: (error: ApiClientError) => boolean | Promise<boolean>;
  onSessionExpiresAt?: (
    expiresAt: components['headers']['X-CA-Session-Expires-At'],
  ) => void;
  /**
   * Whether the user pointed, typed or scrolled since the previous request. Such a request
   * says so, and only those keep the browser session alive: polls, heartbeats and stream
   * reconnects in a tab left open never do.
   */
  isUserActive?: () => boolean;
}

interface SubmitBatchBaseInput {
  correlationId?: string;
  documentFolderPaths?: readonly string[];
  documentIds?: readonly string[];
  documentTags?: readonly string[];
  idempotencyKey?: string;
  projectId: string;
  signal?: AbortSignal;
  timeoutMs?: number | null;
}

export type SubmitBatchInput = SubmitBatchBaseInput &
  (
    | { templateId: string; templateVersionId?: never }
    | { templateId?: never; templateVersionId: string }
  );


interface ImportTemplateWorkbookInput {
  templateType: AuthorableTemplateType;
  workbook: File;
}

interface BillingCostGraphOptions {
  projectId?: string;
  granularity?: 'day' | 'month';
  year?: number;
  month?: number;
}

/**
 * Input for writing a manual override on an analysis cell.
 *
 * `value` and `displayValue` are the editor's wire shapes. The caller owns
 * the JSON shape inside `value` (e.g. `{ text }` for text, `{ value }` for
 * numbers); the backend stores it verbatim.
 */
export interface WriteCellOverrideInput {
  readonly analysisId: string;
  readonly documentId: string;
  readonly analysisFieldId: string;
  readonly value: { readonly [key: string]: unknown };
  readonly displayValue?: string | null;
  readonly baseEffectiveVersion: number;
  readonly note?: string | null;
  readonly auditGroupId?: string | null;
  readonly signal?: AbortSignal;
}

export interface CellReviewInput {
  readonly analysisId: string;
  readonly documentId: string;
  readonly analysisFieldId: string;
  readonly baseEffectiveVersion: number;
  readonly auditGroupId?: string | null;
  readonly signal?: AbortSignal;
}

/**
 * A bulk cell request. One whose serialized body exceeds {@link REQUEST_BODY_MAX_BYTES}
 * is refused before sending with the API's `bulk-cell-mutations-too-large` error; split
 * a best-effort action into several requests instead.
 */
export interface BulkCellMutationsInput {
  readonly analysisId: string;
  readonly atomic?: boolean;
  readonly items: readonly BulkCellMutationItem[];
  readonly signal?: AbortSignal;
}

export interface BulkCellMutationItem {
  readonly documentId: string;
  readonly analysisFieldId: string;
  readonly action: 'override' | 'accept' | 'clear';
  readonly baseEffectiveVersion: number;
  readonly value?: { readonly [key: string]: unknown } | null;
  readonly displayValue?: string | null;
}

export interface AnalysisCellFeedbackTarget {
  readonly analysisId: string;
  readonly documentId: string;
  readonly analysisFieldId: string;
  readonly machineRevisionNumber: number;
}

/**
 * Discriminated result for the three single-cell mutations.
 *
 * 200 → `{ kind: 'ok', detail }`.
 * 409 → `{ kind: 'conflict', conflict }`. The caller's
 * `base_effective_version` was stale; the server returns the latest
 * effective cell so the UI can surface a conflict view.
 *
 * Any other non-OK response throws an `ApiClientError` as usual.
 */
export type CellMutationResult =
  | { readonly kind: 'ok'; readonly detail: AnalysisCellDetailResponse }
  | { readonly kind: 'conflict'; readonly conflict: CellConcurrencyConflictResponse };

/** An analysis export as one read returned it, with the read's polling advice. */
export interface AnalysisExportObservation {
  readonly job: AnalysisExportResponse;
  /** The response's `Retry-After`: read the export again no sooner. `null` once it has finished. */
  readonly retryAfterSeconds: RetryAfterSeconds | null;
}

/**
 * Bytes of an upload session's file for `sendUploadSession`: the whole file, or a part that
 * starts on a block boundary (`transfer.block_size_bytes`) and ends on one or at the end.
 */
export interface UploadSendPart {
  readonly bytes: Blob;
  /** Where `bytes` starts in the file. */
  readonly offset: number;
  /** The file's declared size. */
  readonly fileSizeBytes: number;
}

/**
 * A session renewal: `renewedTenantId` is the workspace the page is working in, which the
 * renewed session keeps. The session it replaces has usually ended by then, so it can no
 * longer say which workspace that was.
 */
export interface SessionRenewal {
  readonly renewedTenantId: string | null;
}

/** Browser-app client surface for interactive contract analysis workflows. */
export interface ContractAnalyzerAppApiClient extends ExtractionRunApiClient {
  /**
   * All methods reject with {@link ApiClientError} or a subclass.
   *
   * - {@link ApiClientValidationError} signals invalid caller input before a request is sent.
   * - {@link ApiClientTransportError} signals that the request could not reach the API.
   * - {@link ApiClientError} signals a non-OK API response.
   */
  acceptTerms(input: AcceptTermsRequest): Promise<UserResponse>;
  adoptProjectAnalysesCapability(projectId: string): Promise<ProjectResponse>;
  archiveProject(projectId: string): Promise<ProjectResponse>;
  approveAccessRequest(requestId: string): Promise<AccessRequestResponse>;
  approveTenantChargeCode(
    tenantId: string,
    chargeCodeId: string,
    input: ReviewChargeCodeRequest,
  ): Promise<ChargeCodeResponse>;
  assignProjectMember(
    projectId: string,
    input: AssignMemberRequest,
  ): Promise<ProjectMemberResponse>;
  approveCliLogin(
    loginId: string,
    input: CliLoginApprovalRequest,
  ): Promise<CliLoginApprovalResponse>;
  denyCliLogin(loginId: string): Promise<CliLoginDenialResponse>;
  /** Starts a session from an identity token; a renewal keeps the page's workspace. */
  bootstrapIdentityFromToken(token: string, renewal?: SessionRenewal): Promise<AuthBootstrapResponse>;
  bootstrapSessionFromIdentityToken(token: string): Promise<AuthSessionResponse>;
  createAnalysis(input: CreateAnalysisRequest): Promise<AnalysisResponse>;
  resolveCanonicalTemplateAnalysis(
    input: ResolveCanonicalAnalysisRequest,
  ): Promise<ResolveCanonicalAnalysisResponse>;
  createTenantChargeCode(
    tenantId: string,
    input: CreateChargeCodeRequest,
  ): Promise<ChargeCodeResponse>;
  createAnalysisRevision(
    analysisId: string,
    input: CreateRevisionRequest,
  ): Promise<AnalysisRevisionResponse>;
  createAnalysisExtraction(
    analysisId: string,
    input: CreateExtractionRequest,
  ): Promise<AnalysisExtractionResponse>;
  previewAnalysisExtraction(
    analysisId: string,
    input: CreateExtractionRequest,
  ): Promise<AnalysisExtractionPreviewResponse>;
  getAnalysisExtractionStatus(analysisId: string): Promise<AnalysisExtractionStatusResponse>;
  createProject(input: ProjectUpsertRequest): Promise<ProjectResponse>;
  createTemplate(input: TemplateCreateRequest): Promise<TemplateDetailResponse>;
  previewTemplate(input: TemplatePreviewRequest): Promise<TemplatePreviewResponse>;
  reviewTemplate(input: TemplateReviewRequest): Promise<TemplateReviewResponse>;
  validateTemplate(
    input: TemplateValidationRequest,
    options?: { readonly signal?: AbortSignal },
  ): Promise<TemplateValidationResponse>;
  createTemplateVersion(
    templateId: string,
    input: TemplateVersionCreateRequest,
  ): Promise<TemplateVersionResponse>;
  createUser(input: CreateUserRequest): Promise<UserResponse>;
  createGroundTruthSample(
    input: GroundTruthSampleCreateRequest,
  ): Promise<GroundTruthSampleResponse>;
  deleteAnalysis(analysisId: string): Promise<void>;
  deleteDocument(documentId: string): Promise<void>;
  deleteProject(projectId: string): Promise<void>;
  deleteTemplate(templateId: string): Promise<void>;
  deleteTrashedDocument(documentId: string): Promise<void>;
  deleteTrashedProject(projectId: string): Promise<void>;
  deleteTrashedTemplate(templateId: string): Promise<void>;
  deleteProjectPresence(projectId: string, presenceId: string): Promise<void>;
  deleteGroundTruthSample(sampleId: string): Promise<void>;
  deleteFolioConversation(conversationId: string): Promise<void>;
  deactivateTenantChargeCode(tenantId: string, chargeCodeId: string): Promise<ChargeCodeResponse>;
  archiveTenant(tenantId: string): Promise<void>;
  restoreTenant(tenantId: string): Promise<TenantResponse>;
  deleteUser(userId: string): Promise<void>;
  reactivateUser(userId: string): Promise<void>;
  restoreAnalysis(analysisId: string): Promise<void>;
  trashEvaluationRun(evalRunId: string): Promise<void>;
  restoreEvaluationRun(evalRunId: string): Promise<void>;
  executeFolioAction(input: FolioActionExecutionRequest): Promise<FolioActionExecutionResponse>;
  downloadExistingTemplateWorkbook(templateId: string): Promise<Response>;
  downloadTemplateWorkbook(templateType: AuthorableTemplateType): Promise<Response>;
  /**
   * Queue a workbook export of the chosen documents, or of every document when neither
   * `document_ids` nor `document_filter` is sent. IDs travel in the POST body so large
   * scopes do not exceed proxy URL limits. An explicitly empty `document_ids` is rejected
   * before any request, so an empty selection never widens to the whole grid. Send one
   * `idempotencyKey` per user submission, and the same key again when retrying it, so a
   * retried request returns the export the first one queued instead of queuing a second.
   */
  createAnalysisExport(
    analysisId: string,
    input: CreateAnalysisExportRequest,
    options?: { readonly idempotencyKey?: string },
  ): Promise<AnalysisExportObservation>;
  /** The caller's most recent exports of the analysis, newest first. */
  listAnalysisExports(analysisId: string): Promise<AnalysisExportListResponse>;
  /** Poll an export; honour `retryAfterSeconds` before reading it again. */
  getAnalysisExport(analysisId: string, exportId: string): Promise<AnalysisExportObservation>;
  cancelAnalysisExport(analysisId: string, exportId: string): Promise<AnalysisExportResponse>;
  /** A succeeded export's workbook, streamed through the API under the caller's session. */
  downloadAnalysisExport(analysisId: string, exportId: string): Promise<Response>;
  exportBillingInvoice(tenantId: string, periodMonth: string): Promise<Response>;
  exportCumulativeBillingInvoice(tenantId: string): Promise<Response>;
  importTemplateWorkbook(
    input: ImportTemplateWorkbookInput,
  ): Promise<TemplateWorkbookImportResponse>;
  /**
   * Download a TSA generation run export as a binary blob. The backend streams
   * a zip of per-service DOCX files or a consolidated XLSX workbook. Legacy
   * `json` requests are preserved as a compatibility alias for XLSX.
   */
  exportTsaGenerationRun(
    generationRunId: string,
    exportFormat?: TsaExportFormat,
  ): Promise<Response>;
  /**
   * Download a single identified-service export from a TSA generation run as
   * a DOCX file. See
   * `/api/v1/tsa/runs/{generation_run_id}/services/{identified_service_id}/export`.
   */
  exportTsaIdentifiedService(
    generationRunId: string,
    identifiedServiceId: string,
    exportFormat?: 'docx' | 'json',
  ): Promise<Response>;
  /**
   * Send a chat message to Folio and receive a streaming SSE response.
   * The caller is responsible for reading the returned {@link Response} body
   * as a `text/event-stream` via `ReadableStream`.
   */
  folioChat(input: FolioChatRequest, options?: { signal?: AbortSignal }): Promise<Response>;
  /** The tenant's activity since `since`; `projectId` narrows it to one project, the caller's own events included. */
  getDashboardActivity(
    tenantId: string,
    options?: { readonly since?: string | null; readonly projectId?: string; readonly signal?: AbortSignal },
  ): Promise<DashboardActivityEnvelopeResponse>;
  /** Every accessible project with its results, documents and live extractions; `projectId` reads one. */
  listDashboardProjects(
    tenantId: string,
    options?: { readonly projectId?: string; readonly signal?: AbortSignal },
  ): Promise<DashboardProjectListResponse>;
  getAnalysis(analysisId: string): Promise<AnalysisResponse>;
  heartbeatAnalysisPresence(
    analysisId: string,
    input: AnalysisPresenceHeartbeatRequest,
  ): Promise<AnalysisPresenceSnapshotResponse>;
  getAnalysisPresence(
    analysisId: string,
    options?: { signal?: AbortSignal },
  ): Promise<AnalysisPresenceListResponse>;
  acquireCellLock(
    analysisId: string,
    documentId: string,
    fieldId: string,
    input: CellLockRequest,
  ): Promise<CellLockResponse | CellLockConflictResponse>;
  releaseCellLock(
    analysisId: string,
    documentId: string,
    fieldId: string,
    input: CellLockReleaseRequest,
  ): Promise<void>;
  listCellLocks(
    analysisId: string,
    options?: { signal?: AbortSignal },
  ): Promise<AnalysisLocksResponse>;
  heartbeatProjectPresence(
    projectId: string,
    input: ProjectPresenceHeartbeatRequest,
  ): Promise<ProjectPresenceSnapshotResponse>;
  getAnalysisGrid(analysisId: string, options?: { revision?: number; signal?: AbortSignal }): Promise<AnalysisGridResponse>;
  getAnalysisFreshnessChanges(analysisId: string, afterEventId: number, options?: { readonly signal?: AbortSignal }): Promise<AnalysisFreshnessChangesResponse>;
  getAnalysisGridMetadata(analysisId: string, options?: { signal?: AbortSignal }): Promise<AnalysisGridMetadataResponse>;
  getAnalysisGridRowsPage(
    analysisId: string,
    options?: {
      cursor?: string;
      limit?: number;
      /** At most 100 documents; the page keeps grid order and omits inactive ones. */
      documentIds?: readonly string[];
      /** At most 500 columns; each row carries only these cells. */
      fieldIds?: readonly string[];
      signal?: AbortSignal;
    },
  ): Promise<AnalysisGridRowsPageResponse>;
  getAnalysisGridRow(
    analysisId: string,
    documentId: string,
    options?: { signal?: AbortSignal },
  ): Promise<AnalysisGridRowSnapshotResponse>;
  getAnalysisCellDetail(
    analysisId: string,
    documentId: string,
    analysisFieldId: string,
    options?: { revision?: number; signal?: AbortSignal },
  ): Promise<AnalysisCellDetailResponse>;
  getAnalysisCellFeedback(
    target: AnalysisCellFeedbackTarget,
  ): Promise<CurrentAiFeedbackResponse>;
  upsertAnalysisCellFeedback(
    target: AnalysisCellFeedbackTarget,
    input: AiFeedbackUpsertRequest,
  ): Promise<CurrentAiFeedbackResponse>;
  retractAnalysisCellFeedback(target: AnalysisCellFeedbackTarget): Promise<void>;
  getAnalysisFieldReviewSummary(
    analysisId: string,
    analysisFieldId: string,
    options?: { signal?: AbortSignal },
  ): Promise<AnalysisFieldReviewSummaryResponse>;
  /**
   * Checked values nobody has reviewed yet, lowest confidence first, in keyset
   * pages that each count the cells behind the whole queue. A cursor belongs to
   * the tier and document filters it was read with.
   */
  listAnalysisReviewQueue(
    analysisId: string,
    options?: {
      /** Omitted, the queue holds low and medium values. */
      tiers?: readonly ConfidenceTier[];
      /**
       * The result grid's document filter, in the shape an export's
       * `document_filter` takes; omitted, the queue covers every document.
       */
      documentFilter?: AnalysisExportDocumentFilterRequest;
      limit?: number;
      cursor?: string;
      signal?: AbortSignal;
    },
  ): Promise<AnalysisReviewQueueResponse>;
  /**
   * Write a manual override on an analysis cell.
   *
   * Returns a discriminated result instead of throwing on 409 so the
   * editor can hand the conflict to the cell-detail rail.
   */
  writeCellOverride(input: WriteCellOverrideInput): Promise<CellMutationResult>;
  /** Accept the machine value for a cell. 409 → discriminated `conflict`. */
  acceptCell(input: CellReviewInput): Promise<CellMutationResult>;
  /** Revert acceptance for a cell. 409 → discriminated `conflict`. */
  revertCellAcceptance(input: CellReviewInput): Promise<CellMutationResult>;
  /**
   * Discard the manual override on a cell so the effective value reverts to
   * the machine extraction. Requires `cells.edit`. 409 → discriminated
   * `conflict`.
   */
  clearCellOverride(input: CellReviewInput): Promise<CellMutationResult>;
  /**
   * Apply a bulk batch of cell mutations (override / accept / clear).
   * Best-effort by default; pass `atomic: true` for all-or-nothing.
   */
  applyBulkCellMutations(input: BulkCellMutationsInput): Promise<BulkCellMutationsResponse>;
  getAnalysisRevision(
    analysisId: string,
    revisionNumber: number,
  ): Promise<AnalysisRevisionResponse>;
  getAuthSession(): Promise<AuthSessionResponse>;
  getBatch(
    batchId: string,
    options?: { includeEvents?: boolean },
  ): Promise<BatchStatusSnapshotResponse>;
  getBatchProcessingLog(
    batchId: string,
    options?: { page?: number; pageSize?: number },
  ): Promise<BatchProcessingLogPageResponse>;
  getBillingPeriodDetail(tenantId: string, periodMonth: string): Promise<BillingPeriodDetailResponse>;
  getBillingSummary(tenantId: string): Promise<BillingSummaryResponse>;
  getBillingWorkspaceCostBreakdown(tenantId: string): Promise<BillingWorkspaceCostBreakdownResponse>;
  getBillingWorkspaceDirectorySummary(tenantId: string): Promise<BillingWorkspaceDirectorySummaryResponse>;
  reconcileUnresolvedProviderCosts(
    tenantId: string,
    input: ProviderCostReconciliationRequest,
  ): Promise<ProviderCostReconciliationResponse>;
  listUnresolvedProviderCosts(
    tenantId: string,
    options?: { cursor?: string; limit?: number },
  ): Promise<UnresolvedProviderCostEventListResponse>;
  listProviderCostCorrections(
    tenantId: string,
    options?: { cursor?: string; limit?: number },
  ): Promise<ProviderCostCorrectionListResponse>;
  getCurrentUser(): Promise<MeResponse>;
  getCurrentUserProjectAccess(): Promise<UserProjectAccessResponse>;
  getDocument(documentId: string): Promise<DocumentDetailResponse>;
  downloadDocument(documentId: string): Promise<Response>;
  getFolioMessageFeedback(messageId: string): Promise<CurrentAiFeedbackResponse>;
  getGroundTruthOutcomeSuggestions(templateVersionId: string): Promise<OutcomeSuggestionsResponse>;
  getHealth(): Promise<HealthResponse>;
  getMyAccessRequestStatus(token: string): Promise<MyAccessRequestStatusResponse>;
  getProject(projectId: string): Promise<ProjectResponse>;
  getProjectPermissions(projectId: string): Promise<ProjectPermissionsResponse>;
  getProjectPresence(projectId: string): Promise<ProjectPresenceSnapshotResponse>;
  getProjectTrash(projectId: string): Promise<ProjectTrashResponse>;
  getProjectUsage(projectId: string, options?: { readonly signal?: AbortSignal }): Promise<UsageSummaryResponse>;
  getProjectDocumentIngestSummary(
    projectId: string,
    options?: { readonly signal?: AbortSignal },
  ): Promise<ProjectDocumentIngestSummaryResponse>;
  getEvaluationRun(evalRunId: string, options?: { readonly signal?: AbortSignal }): Promise<EvaluationRunDetailResponse>;
  getFolioConversation(conversationId: string): Promise<FolioConversationDetailResponse>;
  getFolioGuidance(projectId?: string): Promise<FolioGuidanceResponse>;
  upsertFolioMessageFeedback(
    messageId: string,
    input: AiFeedbackUpsertRequest,
  ): Promise<CurrentAiFeedbackResponse>;
  retractFolioMessageFeedback(messageId: string): Promise<void>;
  getLatestEvaluationRunForRun(runId: string): Promise<EvaluationRunLookupResponse | null>;
  /**
   * Fetch the latest TSA generation status for `(project_id, template_version_id)`.
   * Drives the workspace polling loop while `has_pending_generation` is true.
   */
  getLatestTsaGenerationStatus(
    projectId: string,
    templateVersionId: string,
  ): Promise<TsaGenerationStatusResponse>;
  /** Build the per-run TSA SSE endpoint URL for browser EventSource consumers. */
  getTsaGenerationRunEventsUrl(generationRunId: string): string;
  /** Fetch the latest completed matrix result for the project+template pair. */
  getLatestTsaGenerationRun(
    projectId: string,
    templateVersionId: string,
  ): Promise<TsaGenerationRunDetailResponse>;
  /** Fetch the latest risk assessment for the project+template pair. */
  getLatestTsaRiskAssessment(
    projectId: string,
    templateVersionId: string,
  ): Promise<TsaRiskAssessmentResponse>;
  /** Fetch the latest fact sheet for the project+template pair. */
  getLatestTsaFactSheet(
    projectId: string,
    templateVersionId: string,
  ): Promise<TsaFactSheetResponse>;
  /** Update one field on a TSA fact sheet for a generation run. */
  updateTsaFactSheetField(
    generationRunId: string,
    input: UpdateTsaServiceFieldRequest,
  ): Promise<TsaFactSheetResponse>;
  /** Fetch the latest gap & overlap findings for the project+template pair. */
  getLatestTsaFindings(
    projectId: string,
    templateVersionId: string,
  ): Promise<TsaFindingsResponse>;
  /** Fetch the latest cost benchmarks for the project+template pair. */
  getLatestTsaCosts(
    projectId: string,
    templateVersionId: string,
  ): Promise<TsaCostsResponse>;
  /** Queue or reuse the latest capability refresh batch for the project+template pair. */
  regenerateLatestTsaCapabilityRuns(
    projectId: string,
    templateVersionId: string,
  ): Promise<TsaCapabilityRunsResponse>;
  /** List identified services for a generation run. */
  listTsaIdentifiedServices(generationRunId: string): Promise<TsaIdentifiedServicesResponse>;
  /** Update one field on a TSA service row. */
  updateTsaIdentifiedServiceField(
    generationRunId: string,
    identifiedServiceId: string,
    input: UpdateTsaServiceFieldRequest,
  ): Promise<TsaServiceExtractionResponse>;
  /** Add a new TSA column and queue a field-scoped rerun. */
  addTsaColumn(
    generationRunId: string,
    input: AddTsaColumnRequest,
  ): Promise<TsaGenerationStatusResponse>;
  /** Add a new TSA service and queue an item-scoped rerun. */
  addTsaService(
    generationRunId: string,
    input: AddTsaServiceRequest,
  ): Promise<TsaGenerationStatusResponse>;
  /** Delete one TSA service row from a generation run. */
  deleteTsaIdentifiedService(generationRunId: string, identifiedServiceId: string): Promise<void>;
  /** Fetch risk assessment for a specific generation run. */
  getTsaRiskAssessment(generationRunId: string): Promise<TsaRiskAssessmentResponse>;
  /** Fetch fact sheet for a specific generation run. */
  getTsaFactSheet(generationRunId: string): Promise<TsaFactSheetResponse>;
  /** Fetch findings for a specific generation run. */
  getTsaFindings(generationRunId: string): Promise<TsaFindingsResponse>;
  /** Fetch costs for a specific generation run. */
  getTsaCosts(generationRunId: string): Promise<TsaCostsResponse>;
  /** List analytical capability runs for a generation run. */
  listTsaCapabilityRuns(generationRunId: string): Promise<TsaCapabilityRunsResponse>;
  getRun(runId: string): Promise<RunOverviewResponse>;
  /**
   * Fetch the full TSA generation run.
   * The client synthesizes a legacy `sections/items` view from the backend's
   * raw `extractions` payload for compatibility.
   */
  getTsaGenerationRun(generationRunId: string): Promise<TsaGenerationRunDetailResponse>;
  getRunResultDetail(runId: string, resultId: string): Promise<RunResultDetailResponse>;
  getTenant(tenantId: string): Promise<TenantDetailResponse>;
  getTenantUsage(tenantId: string): Promise<TenantUsageSummaryResponse>;
  getTenantUsageByChargeCode(tenantId: string): Promise<TenantUsageByChargeCodeResponse>;
  /**
   * Fetch the cross-deployment portfolio snapshot. Superadmin only.
   *
   * Billings from internal and demo workspaces are excluded from revenue by the
   * backend; their `notionalBillings` carry the uncorrected figure.
   */
  getControlPlanePortfolio(
    query: ControlPlanePortfolioQuery,
    options?: { signal?: AbortSignal },
  ): Promise<ControlPlaneSnapshotResponse>;
  /** Reclassify a workspace as customer, internal, or demo. */
  setTenantAccountKind(
    tenantId: string,
    input: SetTenantAccountKindRequest,
  ): Promise<TenantResponse>;
  getWorkspacePricing(tenantId: string): Promise<WorkspacePricingResponse>;
  getWorkspaceMarketplaceItems(tenantId: string): Promise<MarketplaceCatalogResponse>;
  listManualCharges(tenantId: string): Promise<ManualChargeListResponse>;
  createManualCharge(
    tenantId: string,
    input: ManualChargeRequest,
  ): Promise<ManualChargeResponse>;
  deleteManualCharge(tenantId: string, manualChargeId: string): Promise<void>;
  markDashboardVisit(tenantId: string): Promise<DashboardVisitResponse>;
  listUserApiKeys(options?: { includeRevoked?: boolean }): Promise<readonly UserApiKeyResponse[]>;
  createUserApiKey(input: CreateUserApiKeyRequest): Promise<CreateUserApiKeyResponse>;
  revokeUserApiKey(keyId: string): Promise<UserApiKeyResponse>;
  getUserApiKeyUsage(keyId: string): Promise<ApiKeyUsageResponse>;
  getUserApiKeyProvisioningContext(): Promise<UserApiKeyProvisioningContextResponse>;
  getTemplate(templateId: string): Promise<TemplateDetailResponse>;
  /** One version by ID, including versions the template's history no longer lists. */
  getTemplateVersion(templateId: string, versionId: string): Promise<TemplateVersionResponse>;
  getUser(userId: string): Promise<WorkspaceMemberResponse>;
  getUserProjectAccess(userId: string): Promise<UserProjectAccessResponse>;
  getWorkspaceCatalog(): Promise<WorkspaceCatalogResponse>;
  getWorkspaceTrash(): Promise<WorkspaceTrashResponse>;
  getPermissionCatalog(): Promise<PermissionCatalogResponse>;
  inviteTenantUser(
    tenantId: string,
    input: InviteTenantUserRequest,
  ): Promise<WorkspaceInvitationResponse>;
  listMyWorkspaceInvitations(): Promise<WorkspaceInvitationListResponse>;
  acceptMyWorkspaceInvitation(invitationId: string): Promise<WorkspaceInvitationResponse>;
  declineMyWorkspaceInvitation(invitationId: string): Promise<WorkspaceInvitationResponse>;
  listPlatformInvitations(): Promise<PlatformInvitationListResponse>;
  revokePlatformInvitation(invitationId: string): Promise<PlatformInvitationResponse>;
  removePlatformInvitation(invitationId: string): Promise<void>;
  acceptMyPlatformInvitation(invitationId: string): Promise<PlatformInvitationResponse>;
  declineMyPlatformInvitation(invitationId: string): Promise<PlatformInvitationResponse>;
  listAnalyses(
    projectId: string,
    options?: { readonly sourceRunId?: string; readonly signal?: AbortSignal },
  ): Promise<readonly AnalysisResponse[]>;
  listAnalysisColumns(analysisId: string): Promise<readonly GridColumnResponse[]>;
  listAnalysisRevisions(analysisId: string, options?: { signal?: AbortSignal }): Promise<readonly AnalysisRevisionResponse[]>;
  listBatches(projectId: string): Promise<readonly BatchListItemResponse[]>;
  listBillingCostGraph(
    tenantId: string,
    options?: BillingCostGraphOptions,
  ): Promise<readonly BillingGraphPointResponse[]>;
  listBillingPeriods(tenantId: string): Promise<readonly BillingPeriodSummaryResponse[]>;
  listBillingProjectTotals(
    tenantId: string,
    options?: { readonly year?: number },
  ): Promise<readonly ProjectCostTotalResponse[]>;
  listDocuments(
    projectId: string,
    options?: {
      readonly documentIds?: readonly string[];
      readonly signal?: AbortSignal;
      readonly page?: number;
      readonly pageSize?: number;
      readonly tags?: readonly string[];
      readonly tsaEnabled?: boolean;
      readonly tsaTags?: readonly string[];
      readonly folderPaths?: readonly string[];
      readonly folderPath?: string;
      readonly q?: string;
      readonly tagMatch?: paths["/api/v1/documents"]["get"]["parameters"]["query"]["tag_match"];
      readonly queryMode?: paths["/api/v1/documents"]["get"]["parameters"]["query"]["q_mode"];
    },
  ): Promise<PaginatedStoredTextDocumentSummaryResponse>;
  resolveDocumentUploadOutcomes(
    projectId: string,
    documentIds: readonly string[],
  ): Promise<DocumentUploadOutcomesResponse>;
  listDocumentTagOptions(
    projectId: string,
    options?: {
      readonly folderPaths?: readonly string[];
      readonly folderPath?: string;
    },
  ): Promise<DocumentTagOptionsResponse>;
  listDocumentFolders(projectId: string): Promise<DocumentFolderTreeResponse>;
  createDocumentFolder(input: DocumentFolderCreateRequest): Promise<DocumentFolderResponse>;
  renameDocumentFolder(input: DocumentFolderRenameRequest): Promise<DocumentFolderResponse>;
  updateDocumentFolder(
    documentId: string,
    input: DocumentFolderUpdateRequest,
  ): Promise<DocumentDetailResponse>;
  bulkMoveDocuments(input: BulkMoveDocumentsRequest): Promise<BulkMoveDocumentsResponse>;
  retryDocumentEmbedding(documentId: string): Promise<DocumentDetailResponse>;
  listEvaluationRuns(projectId: string, options?: { readonly signal?: AbortSignal }): Promise<readonly EvaluationRunResponse[]>;
  listFolioConversations(): Promise<readonly FolioConversationResponse[]>;
  listPendingJoinRequests(): Promise<readonly AccessRequestResponse[]>;
  listPendingNewTenantRequests(): Promise<readonly AccessRequestResponse[]>;
  listBulkProjectPresence(projectIds: readonly string[]): Promise<BulkProjectPresenceResponse>;
  listProjectAutomations(projectId: string): Promise<readonly ProjectAutomationRuleResponse[]>;
  createProjectAutomation(
    projectId: string,
    input: CreateProjectAutomationRuleRequest,
  ): Promise<ProjectAutomationRuleResponse>;
  updateProjectAutomation(
    projectId: string,
    ruleId: string,
    input: UpdateProjectAutomationRuleRequest,
  ): Promise<ProjectAutomationRuleResponse>;
  deleteProjectAutomation(projectId: string, ruleId: string): Promise<void>;
  enableProjectAutomation(
    projectId: string,
    ruleId: string,
  ): Promise<ProjectAutomationRuleResponse>;
  disableProjectAutomation(
    projectId: string,
    ruleId: string,
  ): Promise<ProjectAutomationRuleResponse>;
  listProjectMembers(projectId: string): Promise<ProjectMembersResponse>;
  listAssignableProjectUsers(projectId: string): Promise<AssignableProjectUsersResponse>;
  listProjectTemplates(projectId: string): Promise<readonly TemplateSummaryResponse[]>;
  listProjects(): Promise<readonly ProjectResponse[]>;
  listGroundTruthSamples(
    projectId: string,
    options?: {
      documentId?: string;
      templateVersionId?: string;
    },
  ): Promise<readonly GroundTruthSampleResponse[]>;
  listRunResults(runId: string): Promise<RunResultsResponse>;
  listRuns(projectId?: string, options?: { readonly signal?: AbortSignal }): Promise<readonly RunListItemResponse[]>;
  listSuperadmins(): Promise<readonly UserResponse[]>;
  listTenants(): Promise<readonly TenantResponse[]>;
  listTenantInvitations(
    tenantId: string,
    options?: { readonly statuses?: readonly WorkspaceInvitationStatus[] },
  ): Promise<WorkspaceInvitationListResponse>;
  listTemplates(): Promise<readonly TemplateSummaryResponse[]>;
  /** List TSA generation runs for a `(project_id, template_version_id)` pair, newest first. */
  listTsaGenerationRuns(
    projectId: string,
    templateVersionId: string,
  ): Promise<TsaGenerationRunsResponse>;
  listUsers(): Promise<readonly WorkspaceMemberResponse[]>;
  onboardTenant(input: TenantOnboardingRequest): Promise<TenantOnboardingResponse>;
  previewDocument(documentId: string): Promise<DocumentPreviewResponse>;
  rejectAccessRequest(
    requestId: string,
    input: RejectAccessRequestRequest,
  ): Promise<AccessRequestResponse>;
  rejectTenantChargeCode(
    tenantId: string,
    chargeCodeId: string,
    input: ReviewChargeCodeRequest,
  ): Promise<ChargeCodeResponse>;
  restoreTrashedDocument(documentId: string): Promise<StoredTextDocumentSummaryResponse>;
  restoreTrashedProject(projectId: string): Promise<ProjectResponse>;
  restoreTrashedTemplate(templateId: string): Promise<TemplateResponse>;
  resendTenantInvitation(
    tenantId: string,
    invitationId: string,
  ): Promise<WorkspaceInvitationResponse>;
  revokeTenantInvitation(
    tenantId: string,
    invitationId: string,
  ): Promise<WorkspaceInvitationResponse>;
  removeTenantInvitation(tenantId: string, invitationId: string): Promise<void>;
  removeProjectMember(projectId: string, userId: string): Promise<void>;
  assignTemplateToProject(
    projectId: string,
    templateId: string,
    input?: TemplateAssignRequest | null,
  ): Promise<TemplateDetailResponse>;
  signOut(): Promise<void>;
  submitJoinTenantRequest(
    token: string,
    input: SubmitJoinAccessRequest,
  ): Promise<AccessRequestAcceptedResponse>;
  submitNewTenantRequest(
    token: string,
    input: SubmitNewTenantAccessRequest,
  ): Promise<AccessRequestAcceptedResponse>;
  suggestTenantChargeCodes(
    tenantId: string,
    limit?: number,
  ): Promise<readonly ChargeCodeSuggestionResponse[]>;
  startEvaluationRun(input: EvaluationRunStartRequest): Promise<EvaluationRunDetailResponse>;
  /**
   * Enqueue a TSA generation run. Returns the status snapshot for the new (or
   * already-pending) run. The backend dedupes one in-flight run per
   * `(project_id, template_version_id)`.
   */
  startTsaGeneration(input: StartTsaGenerationRequest): Promise<TsaGenerationStatusResponse>;
  /**
   * Regenerate all or part of an existing TSA run. `scope` controls whether the
   * whole run, a section, an item, or a single field is recomputed.
   */
  regenerateTsaGenerationRun(
    generationRunId: string,
    input: RegenerateTsaGenerationRequest,
  ): Promise<TsaGenerationStatusResponse>;
  submitBatch(input: SubmitBatchInput): Promise<BatchSubmissionReceiptResponse>;
  streamDashboardCatchUp(
    tenantId: string,
    options?: {
      since?: string | null;
      signal?: AbortSignal;
    },
  ): Promise<Response>;
  startDemoMode(): Promise<AuthSessionResponse>;
  stopDemoMode(): Promise<AuthSessionResponse>;
  switchTenant(input: SwitchTenantRequest): Promise<AuthSessionResponse>;
  updateTenantChargeCode(
    tenantId: string,
    chargeCodeId: string,
    input: PatchChargeCodeRequest,
  ): Promise<ChargeCodeResponse>;
  updateProjectCapabilities(
    projectId: string,
    input: ProjectCapabilitiesPatchRequest,
  ): Promise<ProjectResponse>;
  updateProject(projectId: string, input: ProjectUpsertRequest): Promise<ProjectResponse>;
  updateProjectMemberPermissions(
    projectId: string,
    userId: string,
    input: UpdateMemberPermissionsRequest,
  ): Promise<ProjectMemberResponse>;
  updateTenant(tenantId: string, input: PatchTenantRequest): Promise<TenantResponse>;
  setTenantTrial(tenantId: string, input: TenantTrialRequest): Promise<TenantResponse>;
  updateUser(userId: string, input: PatchUserRequest): Promise<UserResponse>;
  updateUserPermissions(
    userId: string,
    input: UpdateMembershipPermissionsRequest,
  ): Promise<UserResponse>;
  updateDocumentTags(
    documentId: string,
    input: DocumentTagsUpdateRequest,
  ): Promise<DocumentDetailResponse>;
  upsertWorkspacePricing(
    tenantId: string,
    input: WorkspacePricingRequest,
  ): Promise<WorkspacePricingResponse>;
  createUploadBatch(projectId: string, input: UploadBatchDeclaration): Promise<UploadBatchReceipt>;
  getUploadBatch(projectId: string, batchId: string): Promise<UploadBatchReceipt>;
  listUploadBatches(projectId: string, cursor?: string): Promise<UploadBatchListResponse>;
  getUploadBatchInput(projectId: string, batchId: string, inputId: string): Promise<UploadBatchInputOutcome>;

  createUploadSession(projectId: string, input: UploadSessionCreateRequest): Promise<AuthorizedUploadResponse>;
  getUploadSession(projectId: string, sessionId: string): Promise<UploadSessionResponse>;
  /**
   * Send file bytes into a receiving session through the API. The session that comes back
   * is still `receiving` until it holds every byte; the send that completes the file
   * finalizes it. A busy service answers `upload-send-busy` with `retryAfterSeconds`.
   */
  sendUploadSession(
    projectId: string,
    sessionId: string,
    part: UploadSendPart,
    options?: { readonly signal?: AbortSignal },
  ): Promise<UploadSessionResponse>;
  cancelUploadSession(projectId: string, sessionId: string): Promise<UploadSessionResponse>;

  getCurrentReleaseNews(): Promise<ReleaseNewsResponse>;
  acknowledgeReleaseNews(version: string): Promise<void>;
  getMyAgentConnection(): Promise<AgentConnectionResponse>;
  dismissMyAgentConnection(): Promise<AgentConnectionResponse>;
  acknowledgeMyAgentConnectionConnected(): Promise<AgentConnectionResponse>;
}

export class ApiClientError extends Error {
  readonly code: string | null;
  readonly details: unknown;
  readonly status: number;
  /** The response's `Retry-After`: send the request again no sooner. `null` when it gave none. */
  readonly retryAfterSeconds: RetryAfterSeconds | null;

  constructor(
    message: string,
    options: {
      code: string | null;
      details: unknown;
      status: number;
      cause?: unknown;
      retryAfterSeconds?: RetryAfterSeconds | null;
    },
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'ApiClientError';
    this.code = options.code;
    this.details = options.details;
    this.status = options.status;
    this.retryAfterSeconds = options.retryAfterSeconds ?? null;
  }
}

export class ApiClientValidationError extends ApiClientError {
  constructor(message: string, options: { code: string; details: unknown }) {
    super(message, {
      code: options.code,
      details: options.details,
      status: 0,
    });
    this.name = 'ApiClientValidationError';
  }
}

export class ApiClientTransportError extends ApiClientError {
  constructor(message: string, options: { cause: unknown; details: unknown }) {
    super(message, {
      cause: options.cause,
      code: 'network-error',
      details: options.details,
      status: 0,
    });
    this.name = 'ApiClientTransportError';
  }
}

const APP_SESSION_AUTH_ERROR_CODES: ReadonlySet<string> = new Set([
  'authentication-required',
  'invalid-session',
]);

export function isAppSessionAuthFailure(error: unknown): error is ApiClientError {
  return (
    error instanceof ApiClientError &&
    error.status === 401 &&
    error.code !== null &&
    APP_SESSION_AUTH_ERROR_CODES.has(error.code)
  );
}

function isErrorResponse(value: unknown): value is ErrorResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return 'code' in value && 'message' in value;
}

function isDetailErrorResponse(value: unknown): value is { readonly detail: string } {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return 'detail' in value && typeof value['detail'] === 'string';
}

function isCellConcurrencyConflict(value: unknown): value is CellConcurrencyConflictResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as { current_effective?: unknown; code?: unknown; message?: unknown };
  if (typeof candidate.code !== 'string' || typeof candidate.message !== 'string') {
    return false;
  }
  const effective = candidate.current_effective;
  if (typeof effective !== 'object' || effective === null) {
    return false;
  }
  return 'effective_version' in effective && 'document_id' in effective;
}

export function isCellLockConflict(value: unknown): value is CellLockConflictResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as { conflict?: unknown; current_holder?: unknown };
  if (candidate.conflict !== true) {
    return false;
  }
  const holder = candidate.current_holder;
  if (typeof holder !== 'object' || holder === null) {
    return false;
  }
  return (
    'lock_id' in holder &&
    'presence_id' in holder &&
    'expires_at' in holder &&
    'field_id' in holder
  );
}

async function withConflictResult(
  promise: Promise<AnalysisCellDetailResponse>,
): Promise<CellMutationResult> {
  try {
    const detail = await promise;
    return { kind: 'ok', detail };
  } catch (error: unknown) {
    if (
      error instanceof ApiClientError &&
      error.status === 409 &&
      isCellConcurrencyConflict(error.details)
    ) {
      return { kind: 'conflict', conflict: error.details };
    }
    throw error;
  }
}

type BatchProcessingLogPageEnvelope = BatchProcessingLogPageResponse;


type TemplateWorkbookImportEnvelope = TemplateWorkbookImportResponse;

function isBatchProcessingLogPageEnvelope(value: unknown): value is BatchProcessingLogPageEnvelope {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return (
    'items' in value &&
    'page' in value &&
    'page_size' in value &&
    'total_items' in value &&
    'total_pages' in value &&
    'has_next_page' in value &&
    'has_previous_page' in value &&
    'extracted_files' in value
  );
}

function normalizeBatchProcessingLogPageResponse(
  value: unknown,
): BatchProcessingLogPageResponse | null {
  if (!isBatchProcessingLogPageEnvelope(value)) {
    return null;
  }

  return value;
}


function isTemplateWorkbookImportEnvelope(value: unknown): value is TemplateWorkbookImportEnvelope {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return 'template_schema' in value && 'template_type' in value;
}

function normalizeTemplateWorkbookImportResponse(
  value: unknown,
): TemplateWorkbookImportResponse | null {
  if (!isTemplateWorkbookImportEnvelope(value)) {
    return null;
  }

  return value;
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
}

function resolveRequestUrl(baseUrl: string, path: string): string {
  const requestPath = `${baseUrl}${path}`;
  if (requestPath.startsWith('http://') || requestPath.startsWith('https://')) {
    return requestPath;
  }

  const browserOrigin =
    typeof window !== 'undefined' && window.location.origin && window.location.origin !== 'null'
      ? window.location.origin
      : 'http://localhost';

  return new URL(requestPath, browserOrigin).toString();
}

/**
 * The wire body of a bulk cell request exactly as `applyBulkCellMutations` sends it, so
 * a caller splitting an action can measure what each request will carry.
 */
export function toBulkCellMutationsRequest(
  input: Pick<BulkCellMutationsInput, 'atomic' | 'items'>,
): BulkCellMutationsRequest {
  return {
    atomic: input.atomic ?? false,
    items: input.items.map((item) => {
      const wire: BulkCellMutationItemRequest = {
        action: item.action,
        analysis_field_id: item.analysisFieldId,
        base_effective_version: item.baseEffectiveVersion,
        document_id: item.documentId,
      };
      if (item.value !== undefined) wire.value = item.value;
      if (item.displayValue !== undefined) wire.display_value = item.displayValue;
      return wire;
    }),
  };
}
function buildClientValidationError(
  message: string,
  options: {
    code: string;
    details: unknown;
  },
): ApiClientValidationError {
  return new ApiClientValidationError(message, options);
}

function buildSubmitBatchRequest(input: SubmitBatchInput): SubmitBatchRequest {
  const documentIds = input.documentIds ?? [];
  const documentTags = input.documentTags ?? [];
  const documentFolderPaths = input.documentFolderPaths ?? [];

  if (documentIds.length === 0 && documentTags.length === 0 && documentFolderPaths.length === 0) {
    throw buildClientValidationError(
      'Select at least one document ID, document tag filter, or folder path to submit a batch.',
      {
        code: 'empty-batch-submission',
        details: {
          field: 'document_ids',
        },
      },
    );
  }
  if ((input.templateId === undefined) === (input.templateVersionId === undefined)) {
    throw buildClientValidationError('Provide exactly one template ID or template version ID.', {
      code: 'invalid-template-selection',
      details: { field: 'templateId' },
    });
  }

  const correlationId = input.correlationId?.trim();
  const templateSelector =
    input.templateId === undefined
      ? { template_version_id: input.templateVersionId }
      : { template_id: input.templateId };
  return {
    project_id: input.projectId,
    ...templateSelector,
    document_ids: documentIds.length > 0 ? [...documentIds] : null,
    document_tags: documentTags.length > 0 ? [...documentTags] : null,
    document_folder_paths: documentFolderPaths.length > 0 ? [...documentFolderPaths] : null,
    correlation_id: correlationId === undefined || correlationId === '' ? null : correlationId,
  };
}

interface RequestTimeoutOptions {
  readonly signal?: AbortSignal | undefined;
  readonly timeoutMs?: number | null;
}

interface RequestExecutionScope {
  readonly cleanup: () => void;
  readonly signal: AbortSignal | undefined;
  readonly timedOut: () => boolean;
}

function createTimeoutError(): Error {
  const error = new Error('The API request timed out.');
  error.name = 'TimeoutError';
  return error;
}

function createRequestExecutionScope(options: RequestTimeoutOptions): RequestExecutionScope {
  const timeoutMs = options.timeoutMs;
  const sourceSignal = options.signal;

  if (sourceSignal === undefined && timeoutMs == null) {
    return {
      cleanup: () => {},
      signal: undefined,
      timedOut: () => false,
    };
  }

  const controller = new AbortController();
  let timedOut = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const abortFromSource = (): void => {
    controller.abort(sourceSignal?.reason);
  };

  if (sourceSignal?.aborted === true) {
    controller.abort(sourceSignal.reason);
  } else if (sourceSignal !== undefined) {
    sourceSignal.addEventListener('abort', abortFromSource, { once: true });
  }

  if (timeoutMs != null && Number.isFinite(timeoutMs) && timeoutMs >= 0) {
    timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort(createTimeoutError());
    }, timeoutMs);
  }

  return {
    cleanup: () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      if (sourceSignal !== undefined) {
        sourceSignal.removeEventListener('abort', abortFromSource);
      }
    },
    signal: controller.signal,
    timedOut: () => timedOut,
  };
}

/**
 * Run a request under the caller's signal and a deadline that stay armed while
 * the response body is read, so a stalled body cannot outlive either. Failures
 * that are not API errors surface as transport errors, a spent deadline as a
 * timeout.
 */
async function withRequestDeadline<T>(
  path: string,
  options: RequestTimeoutOptions,
  run: (signal: AbortSignal | undefined) => Promise<T>,
): Promise<T> {
  const scope = createRequestExecutionScope(options);
  try {
    return await run(scope.signal);
  } catch (error: unknown) {
    if (scope.timedOut()) {
      throw new ApiClientTransportError('The API request timed out.', {
        cause: error,
        details: { path, timeoutMs: options.timeoutMs ?? null },
      });
    }
    if (error instanceof ApiClientError) throw error;
    throw new ApiClientTransportError('The API request could not be sent.', {
      cause: error,
      details: { path },
    });
  } finally {
    scope.cleanup();
  }
}

async function parseResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 304) {
    return null;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    const payloadText = await response.text();
    if (!payloadText) {
      return null;
    }

    try {
      return JSON.parse(payloadText) as unknown;
    } catch {
      return payloadText;
    }
  }

  if (contentType.startsWith('text/')) {
    return await response.text();
  }

  return null;
}

const PERMISSION_DENIED_MESSAGE =
  'You do not have permission to perform this action. Please contact the workspace administrator to request access.';

function toApiClientError(status: number, payload: unknown): ApiClientError {
  if (status === 403) {
    return new ApiClientError(PERMISSION_DENIED_MESSAGE, {
      code: isErrorResponse(payload) ? payload.code : null,
      details: payload,
      status,
    });
  }

  if (isErrorResponse(payload)) {
    return new ApiClientError(payload.message, {
      code: payload.code,
      details: payload,
      status,
    });
  }

  if (isDetailErrorResponse(payload)) {
    return new ApiClientError(payload.detail, {
      code: null,
      details: payload,
      status,
    });
  }

  if (status === 413) {
    return new ApiClientError('The selected upload exceeded the current upload limit.', {
      code: null,
      details: payload,
      status,
    });
  }

  // Gateway and shutdown responses carry proxy HTML or an empty body, never product copy, so the
  // raw payload stays in `details` for diagnostics and is never shown to the user.
  if (status === 502 || status === 503 || status === 504) {
    return new ApiClientError(
      'The service is temporarily unavailable. Wait a moment, then try again.',
      {
        code: null,
        details: payload,
        status,
      },
    );
  }

  if (typeof payload === 'string' && payload.trim().length > 0) {
    return new ApiClientError(payload, {
      code: null,
      details: payload,
      status,
    });
  }

  return new ApiClientError('The API request failed.', {
    code: null,
    details: payload,
    status,
  });
}

/** The error with the `Retry-After` its response named, for a caller that must wait before sending again. */
function withRetryAfter(error: ApiClientError, response: Response): ApiClientError {
  const retryAfterSeconds = parseRetryAfterSeconds(response.headers.get('Retry-After'));
  if (retryAfterSeconds === null) return error;
  return new ApiClientError(error.message, {
    code: error.code,
    details: error.details,
    status: error.status,
    retryAfterSeconds,
  });
}

function normalizeIdentityToken(token: string): string {
  const normalizedToken = token.trim();
  if (!normalizedToken) {
    throw buildClientValidationError('A valid sign-in token is required to create a session.', {
      code: 'missing-identity-token',
      details: {
        field: 'token',
      },
    });
  }

  return normalizedToken;
}

function encodePathSegment(value: string): string {
  return encodeURIComponent(value);
}

function buildQueryString(params: Record<string, string>): string {
  return new URLSearchParams(params).toString();
}

const OCTET_STREAM = 'application/octet-stream';

function normalizeOutgoingJsonBody(path: string, method: string, bodyText: string): string {
  if (method !== 'POST' || !/^\/tsa\/runs\/[^/]+\/services$/.test(path)) {
    return bodyText;
  }

  return bodyText
    .replace(/,\s*"force_add":false(?=\s*[}])/u, '')
    .replace(/"force_add":false,\s*/u, '');
}

function adaptTsaGenerationRunDetailResponse(
  detail: TsaGenerationRunDetailApiResponse,
): TsaGenerationRunDetailResponse {
  const sectionId = `${detail.run.generation_run_id}:service_sheets`;
  const items = [...detail.extractions]
    .sort((left, right) => left.ordinal - right.ordinal)
    .map((extraction): TsaSectionItemResponse => {
      const itemId = extraction.service_extraction_id;
      return {
        confidence: null,
        created_at: extraction.created_at,
        display_name: extraction.stable_item_key,
        field_values: extraction.field_values.map((fieldValue): TsaFieldValueResponse => {
          const fieldValueId = `${itemId}:${fieldValue.field_key}`;
          return {
            citations: fieldValue.citations.map((citation, index): TsaFieldCitationResponse => ({
              context_after: null,
              context_before: null,
              created_at: extraction.created_at,
              document_id: citation.document_id,
              end_offset: null,
              excerpt: citation.excerpt,
              page_number: citation.page_number ?? null,
              paragraph_id: citation.paragraph_id ?? null,
              start_offset: null,
              tsa_field_citation_id: `${fieldValueId}:citation:${index.toString()}`,
              tsa_field_value_id: fieldValueId,
            })),
            confidence: fieldValue.confidence ?? null,
            created_at: extraction.created_at,
            display_name: fieldValue.display_name,
            display_value: fieldValue.display_value ?? null,
            field_key: fieldValue.field_key,
            rationale: fieldValue.rationale ?? null,
            tsa_field_value_id: fieldValueId,
            tsa_section_item_id: itemId,
            value_json: fieldValue.value ?? fieldValue.display_value ?? null,
          };
        }),
        ordinal: extraction.ordinal,
        rationale: null,
        stable_item_key: extraction.stable_item_key,
        tsa_section_id: sectionId,
        tsa_section_item_id: itemId,
      };
    });

  const sections: TsaSectionResponse[] = [
    {
      created_at: detail.run.created_at,
      display_name: 'Service Sheet',
      generation_run_id: detail.run.generation_run_id,
      is_repeating: true,
      items,
      ordinal: 0,
      section_key: 'service_sheets',
      tsa_section_id: sectionId,
    },
  ];

  return {
    ...detail,
    sections,
  };
}

export function createContractAnalyzerAppApiClient(
  options: ContractAnalyzerAppApiClientOptions = {},
): ContractAnalyzerAppApiClient {
  const baseUrl = normalizeBaseUrl(options.baseUrl ?? DEFAULT_API_BASE_URL);
  const defaultHeaders = new Headers(options.headers);

  function buildRequestError(status: number, payload: unknown): ApiClientError {
    return toApiClientError(status, payload);
  }

  async function recoverFromAuthFailure(status: number, payload: unknown): Promise<boolean> {
    const error = toApiClientError(status, payload);
    if (!isAppSessionAuthFailure(error)) {
      return false;
    }

    return (await options.onAuthFailure?.(error)) === true;
  }

  async function retryAfterAuthRecovery(
    response: Response,
    retryRequest: () => Promise<Response>,
  ): Promise<Response> {
    if (response.ok) {
      publishSessionDeadline(response);
      return response;
    }

    const payload = await parseResponseBody(response.clone());
    if (await recoverFromAuthFailure(response.status, payload)) {
      const retried = await retryRequest();
      if (retried.ok) publishSessionDeadline(retried);
      return retried;
    }

    return response;
  }

  function publishSessionDeadline(response: Response): void {
    const header = response.headers.get(SESSION_DEADLINE_HEADER);
    if (header === null) return;
    const expiresAt: components['headers']['X-CA-Session-Expires-At'] = Number(header);
    if (Number.isSafeInteger(expiresAt) && expiresAt > 0) {
      options.onSessionExpiresAt?.(expiresAt);
    }
  }

  /** Every request carries the client's defaults, its own headers, and whether the user acted. */
  function buildRequestHeaders(own: HeadersInit | undefined): Headers {
    const headers = new Headers(defaultHeaders);
    if (own !== undefined) {
      new Headers(own).forEach((value, key) => {
        headers.set(key, value);
      });
    }
    if (!headers.has('accept')) {
      headers.set('accept', 'application/json');
    }
    if (options.isUserActive?.() === true) {
      headers.set(USER_ACTIVITY_HEADER, '1');
    }
    return headers;
  }

  /**
   * Send a typed request under `signal`. A Request's own signal only follows the caller's
   * signal while the Request stays reachable; Node's fetch holds that link weakly, so a
   * deadline that must outlive the Request (while its response body is read) is passed
   * here directly instead.
   */
  async function sendRequestWithSignal(request: Request, signal: AbortSignal): Promise<Response> {
    const parsed = new URL(request.url);
    const strippedPath = parsed.pathname.startsWith(ROUTING_PREFIX)
      ? parsed.pathname.slice(ROUTING_PREFIX.length)
      : parsed.pathname;
    const finalUrl = resolveRequestUrl(baseUrl, `${strippedPath}${parsed.search}`);

    const headers = buildRequestHeaders(request.headers);

    const method = request.method.toUpperCase();
    const init: RequestInit = {
      method,
      headers,
      credentials: options.credentials ?? 'include',
    };

    if (request.body !== null) {
      // File bytes go out as they are; only JSON bodies are read as text.
      init.body = request.headers.get('content-type') === OCTET_STREAM
        ? await request.blob()
        : normalizeOutgoingJsonBody(strippedPath, method, await request.text());
    }
    init.signal = signal;
    if (method === 'GET' || method === 'HEAD') {
      init.cache = 'no-store';
    }

    const userFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

    async function executeRequest(): Promise<Response> {
      return await userFetch(finalUrl, init);
    }

    try {
      return await retryAfterAuthRecovery(await executeRequest(), executeRequest);
    } catch (error: unknown) {
      throw new ApiClientTransportError('The API request could not be sent.', {
        cause: error,
        details: {
          path: strippedPath,
        },
      });
    }
  }

  function customFetch(request: Request): Promise<Response> {
    return sendRequestWithSignal(request, request.signal);
  }

  // Middleware that converts openapi-fetch's { data, error, response } discriminated result
  // into an exception on non-OK responses, so method bodies can return `data` directly.
  const throwOnError: Middleware = {
    async onResponse({ response }: { response: Response }) {
      if (!response.ok) {
        const payload = await parseResponseBody(response.clone());
        throw withRetryAfter(buildRequestError(response.status, payload), response);
      }
      return undefined;
    },
  };

  const clientOptions: Parameters<typeof createClient<paths>>[0] = {
    baseUrl: INTERNAL_BASE_URL,
    fetch: customFetch,
  };
  if (options.headers !== undefined) {
    clientOptions.headers = options.headers;
  }
  const api = createClient<paths>(clientOptions);
  api.use(throwOnError);

  /** Raw requests (SSE, downloads, multipart workbook import) stream their own bodies. */
  async function sendRequest(path: string, init: RequestInit): Promise<Response> {
    const fetchImplementation = options.fetch ?? globalThis.fetch.bind(globalThis);
    const headers = buildRequestHeaders(init.headers);
    const method = (init.method ?? 'GET').toUpperCase();
    const cacheMode =
      init.cache ?? (method === 'GET' || method === 'HEAD' ? 'no-store' : undefined);

    async function executeRequest(): Promise<Response> {
      const requestInit: RequestInit = {
        ...init,
        credentials: init.credentials ?? options.credentials ?? 'include',
        headers,
      };
      if (cacheMode !== undefined) {
        requestInit.cache = cacheMode;
      }

      return await fetchImplementation(resolveRequestUrl(baseUrl, path), requestInit);
    }

    try {
      return await retryAfterAuthRecovery(await executeRequest(), executeRequest);
    } catch (error: unknown) {
      if (error instanceof ApiClientError) throw error;
      throw new ApiClientTransportError('The API request could not be sent.', {
        cause: error,
        details: {
          path,
        },
      });
    }
  }

  async function requestResponse(path: string, init: RequestInit): Promise<Response> {
    const response = await sendRequest(path, init);
    if (!response.ok) {
      const payload = await parseResponseBody(response);
      throw withRetryAfter(buildRequestError(response.status, payload), response);
    }
    return response;
  }

  // The wrappers below are mechanical passthroughs onto openapi-fetch. The middleware
  // above converts non-OK responses into thrown ApiClientErrors, so on success
  // `data` is defined; callers receive the generated response type.
  async function unwrapJson<T>(
    promise: Promise<{ data?: T; error?: unknown; response: Response }>,
  ): Promise<T> {
    const { data } = await promise;
    // `data` is guaranteed to be set on success by the throw-on-error middleware.
    if (data === undefined) {
      throw new ApiClientError('The API response did not include a body.', {
        code: null,
        details: null,
        status: 0,
      });
    }
    return data;
  }

  /** A polled export route: the job, plus the `Retry-After` the JSON body cannot carry. */
  async function unwrapExportObservation(
    promise: Promise<{ data?: AnalysisExportResponse; error?: unknown; response: Response }>,
  ): Promise<AnalysisExportObservation> {
    const result = await promise;
    return {
      job: await unwrapJson(Promise.resolve(result)),
      retryAfterSeconds: parseRetryAfterSeconds(result.response.headers.get('Retry-After')),
    };
  }

  async function unwrapVoid(
    promise: Promise<{ data?: unknown; error?: unknown; response: Response }>,
  ): Promise<void> {
    await promise;
  }

  const authorizationHeader = (token: string): { authorization: string } => ({
    authorization: `Bearer ${token}`,
  });

  // The backend's `/runs/latest/<suffix>` convenience routes are shadowed by
  // the generic `/runs/{generation_run_id}/<suffix>` patterns declared first,
  // so a direct call would 422 on "latest" failing UUID validation. Resolve
  // the run id via the status route (no collision — single segment), then
  // hit the per-run route. Throws 404 if no run exists yet.
  async function resolveLatestTsaRunId(
    projectId: string,
    templateVersionId: string,
  ): Promise<string> {
    const status = await unwrapJson(
      api.GET('/api/v1/tsa/runs/latest', {
        params: { query: { project_id: projectId, template_version_id: templateVersionId } },
      }),
    );
    const runId = status.latest_run?.generation_run_id;
    if (!runId) {
      throw new ApiClientError(
        'No TSA generation run available for this project + template version.',
        { code: 'not-found', details: null, status: 404 },
      );
    }
    return runId;
  }

  return {
    async acceptTerms(input) {
      return unwrapJson(api.POST('/api/v1/users/me/terms', { body: input }));
    },
    async approveAccessRequest(requestId) {
      return unwrapJson(
        api.POST('/api/v1/access-requests/{request_id}/approve', {
          params: { path: { request_id: requestId } },
        }),
      );
    },
    async approveTenantChargeCode(tenantId, chargeCodeId, input) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/charge-codes/{charge_code_id}/approve', {
          params: { path: { tenant_id: tenantId, charge_code_id: chargeCodeId } },
          body: input,
        }),
      );
    },
    async assignProjectMember(projectId, input) {
      return unwrapJson(
        api.POST('/api/v1/projects/{project_id}/members', {
          params: { path: { project_id: projectId } },
          body: input,
        }),
      );
    },
    async approveCliLogin(loginId, input) {
      return unwrapJson(
        api.POST('/api/v1/auth/cli-login/{login_id}/approve', {
          params: { path: { login_id: loginId } },
          body: input,
        }),
      );
    },
    async denyCliLogin(loginId) {
      return unwrapJson(
        api.POST('/api/v1/auth/cli-login/{login_id}/deny', {
          params: { path: { login_id: loginId } },
        }),
      );
    },
    async bootstrapIdentityFromToken(token, renewal) {
      const normalizedToken = normalizeIdentityToken(token);
      return unwrapJson(
        api.POST('/api/v1/auth/bootstrap', {
          headers: authorizationHeader(normalizedToken),
          ...(renewal === undefined ? {} : { body: { tenant_id: renewal.renewedTenantId } }),
        }),
      );
    },
    async bootstrapSessionFromIdentityToken(token) {
      const normalizedToken = normalizeIdentityToken(token);
      const user = await unwrapJson<SessionBootstrapResponse>(
        api.POST('/api/v1/auth/signin', {
          headers: authorizationHeader(normalizedToken),
        }),
      );
      // The /auth/signin route returns SessionBootstrapResponse; the legacy client-side
      // contract wraps it into the AuthSessionResponse shape that existing consumers expect.
      return {
        authenticated: true,
        available_memberships: [],
        pending_invitations: user.pending_invitations,
        pending_platform_invitations: user.pending_platform_invitations,
        user,
      };
    },
    async createAnalysis(input) {
      return unwrapJson(api.POST('/api/v1/analyses', { body: input }));
    },
    async resolveCanonicalTemplateAnalysis(input) {
      return unwrapJson(api.POST('/api/v1/analyses/resolve', { body: input }));
    },
    async createTenantChargeCode(tenantId, input) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/charge-codes', {
          params: { path: { tenant_id: tenantId } },
          body: input,
        }),
      );
    },
    async createAnalysisRevision(analysisId, input) {
      return unwrapJson(
        api.POST('/api/v1/analyses/{analysis_id}/revisions', {
          params: { path: { analysis_id: analysisId } },
          body: input,
        }),
      );
    },
    async createAnalysisExtraction(analysisId, input) {
      return unwrapJson(
        api.POST('/api/v1/analyses/{analysis_id}/extractions', {
          params: { path: { analysis_id: analysisId } },
          body: input,
        }),
      );
    },
    async previewAnalysisExtraction(analysisId, input) {
      return unwrapJson(
        api.POST('/api/v1/analyses/{analysis_id}/extractions/preview', {
          params: { path: { analysis_id: analysisId } },
          body: input,
        }),
      );
    },
    async getAnalysisExtractionStatus(analysisId) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/extractions/status', {
          params: { path: { analysis_id: analysisId } },
        }),
      );
    },
    async createProject(input) {
      return unwrapJson(api.POST('/api/v1/projects', { body: input }));
    },
    async adoptProjectAnalysesCapability(projectId) {
      return unwrapJson(
        api.POST('/api/v1/projects/{project_id}/capabilities/analyses', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async archiveProject(projectId) {
      return unwrapJson(
        api.POST('/api/v1/projects/{project_id}/archive', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async createTemplate(input) {
      return unwrapJson(api.POST('/api/v1/templates', { body: input }));
    },
    async previewTemplate(input) {
      return unwrapJson(api.POST('/api/v1/templates/preview', { body: input }));
    },
    async reviewTemplate(input) {
      return unwrapJson(api.POST('/api/v1/templates/review', { body: input }));
    },
    async validateTemplate(input, options) {
      return unwrapJson(
        api.POST('/api/v1/templates/validate', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          body: input,
        }),
      );
    },
    async createGroundTruthSample(input) {
      return unwrapJson(api.POST('/api/v1/ground-truth', { body: input }));
    },
    async createTemplateVersion(templateId, input) {
      return unwrapJson(
        api.POST('/api/v1/templates/{template_id}/versions', {
          params: { path: { template_id: templateId } },
          body: input,
        }),
      );
    },
    async createUser(input) {
      return unwrapJson(api.POST('/api/v1/users', { body: input }));
    },
    async deleteAnalysis(analysisId) {
      await unwrapVoid(
        api.POST('/api/v1/analyses/{analysis_id}', {
          params: { path: { analysis_id: analysisId } },
        }),
      );
    },
    async restoreAnalysis(analysisId) {
      await unwrapVoid(
        api.POST('/api/v1/analyses/{analysis_id}/restore', {
          params: { path: { analysis_id: analysisId } },
        }),
      );
    },
    async deleteDocument(documentId) {
      await unwrapVoid(
        api.POST('/api/v1/documents/{document_id}/delete', {
          params: { path: { document_id: documentId } },
        }),
      );
    },
    async deleteProject(projectId) {
      await unwrapVoid(
        api.POST('/api/v1/projects/{project_id}/delete', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async deleteTemplate(templateId) {
      await unwrapVoid(
        api.POST('/api/v1/templates/{template_id}/delete', {
          params: { path: { template_id: templateId } },
        }),
      );
    },
    async deleteTrashedDocument(documentId) {
      await unwrapVoid(
        api.POST('/api/v1/workspace/trash/documents/{document_id}', {
          params: { path: { document_id: documentId } },
        }),
      );
    },
    async deleteTrashedProject(projectId) {
      await unwrapVoid(
        api.POST('/api/v1/workspace/trash/projects/{project_id}', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async deleteTrashedTemplate(templateId) {
      await unwrapVoid(
        api.POST('/api/v1/workspace/trash/templates/{template_id}', {
          params: { path: { template_id: templateId } },
        }),
      );
    },
    async deleteProjectPresence(projectId, presenceId) {
      await unwrapVoid(
        api.POST('/api/v1/projects/{project_id}/presence/{presence_id}', {
          params: { path: { project_id: projectId, presence_id: presenceId } },
        }),
      );
    },
    async archiveTenant(tenantId) {
      await unwrapVoid(
        api.POST('/api/v1/tenants/{tenant_id}/archive', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async restoreTenant(tenantId) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/restore', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async deleteUser(userId) {
      await unwrapVoid(
        api.POST('/api/v1/users/{user_id}', {
          params: { path: { user_id: userId } },
        }),
      );
    },
    async reactivateUser(userId) {
      await unwrapVoid(
        api.POST('/api/v1/users/{user_id}/reactivate', {
          params: { path: { user_id: userId } },
        }),
      );
    },
    async getAnalysis(analysisId) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}', {
          params: { path: { analysis_id: analysisId } },
        }),
      );
    },
    async heartbeatAnalysisPresence(analysisId, input) {
      return unwrapJson(
        api.POST('/api/v1/analyses/{analysis_id}/presence', {
          params: { path: { analysis_id: analysisId } },
          body: input,
        }),
      );
    },
    async getAnalysisPresence(analysisId, options) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/presence', {
          params: { path: { analysis_id: analysisId } },
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
        }),
      );
    },
    async acquireCellLock(analysisId, documentId, fieldId, input) {
      try {
        return await unwrapJson(
          api.POST(
            '/api/v1/analyses/{analysis_id}/cells/{document_id}/{analysis_field_id}/lock',
            {
              params: {
                path: {
                  analysis_id: analysisId,
                  document_id: documentId,
                  analysis_field_id: fieldId,
                },
              },
              body: input,
            },
          ),
        );
      } catch (error: unknown) {
        if (
          error instanceof ApiClientError &&
          error.status === 409 &&
          isCellLockConflict(error.details)
        ) {
          return error.details;
        }
        throw error;
      }
    },
    async releaseCellLock(analysisId, documentId, fieldId, input) {
      await unwrapVoid(
        api.POST(
          '/api/v1/analyses/{analysis_id}/cells/{document_id}/{analysis_field_id}/lock/release',
          {
            params: {
              path: {
                analysis_id: analysisId,
                document_id: documentId,
                analysis_field_id: fieldId,
              },
            },
            body: input,
          },
        ),
      );
    },
    async listCellLocks(analysisId, options) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/locks', {
          params: { path: { analysis_id: analysisId } },
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
        }),
      );
    },
    async heartbeatProjectPresence(projectId, input) {
      return unwrapJson(
        api.POST('/api/v1/projects/{project_id}/presence', {
          params: { path: { project_id: projectId } },
          body: input,
        }),
      );
    },
    async getAnalysisGrid(analysisId, options) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/grid', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: {
            path: { analysis_id: analysisId },
            query: options?.revision === undefined ? {} : { revision: options.revision },
          },
        }),
      );
    },
    async getAnalysisFreshnessChanges(analysisId, afterEventId, options) {
      return unwrapJson(api.GET('/api/v1/analyses/{analysis_id}/changes', {
        ...(options?.signal === undefined ? {} : { signal: options.signal }),
        params: { path: { analysis_id: analysisId }, query: { after_event_id: afterEventId } },
      }));
    },
    async getAnalysisGridMetadata(analysisId, options) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/grid/metadata', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: { path: { analysis_id: analysisId } },
        }),
      );
    },
    async getAnalysisGridRowsPage(analysisId, options) {
      const query: paths['/api/v1/analyses/{analysis_id}/grid/rows']['get']['parameters']['query'] = {};
      if (options?.cursor !== undefined) query.cursor = options.cursor;
      if (options?.limit !== undefined) query.limit = options.limit;
      if (options?.documentIds !== undefined) query.document_id = [...options.documentIds];
      if (options?.fieldIds !== undefined) query.field_id = [...options.fieldIds];
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/grid/rows', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: {
            path: { analysis_id: analysisId },
            query,
          },
        }),
      );
    },
    async getAnalysisGridRow(analysisId, documentId, options) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/grid/rows/{document_id}', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: {
            path: {
              analysis_id: analysisId,
              document_id: documentId,
            },
          },
        }),
      );
    },
    async getAnalysisCellDetail(analysisId, documentId, analysisFieldId, options) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/cells/{document_id}/{analysis_field_id}', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: {
            path: {
              analysis_id: analysisId,
              document_id: documentId,
              analysis_field_id: analysisFieldId,
            },
            query: options?.revision === undefined ? {} : { revision: options.revision },
          },
        }),
      );
    },
    async getAnalysisCellFeedback(target) {
      return unwrapJson(
        api.GET(
          '/api/v1/analyses/{analysis_id}/cells/{document_id}/{analysis_field_id}/feedback',
          {
            params: {
              path: {
                analysis_id: target.analysisId,
                document_id: target.documentId,
                analysis_field_id: target.analysisFieldId,
              },
              query: { machine_revision_number: target.machineRevisionNumber },
            },
          },
        ),
      );
    },
    async upsertAnalysisCellFeedback(target, input) {
      return unwrapJson(
        api.PUT(
          '/api/v1/analyses/{analysis_id}/cells/{document_id}/{analysis_field_id}/feedback',
          {
            params: {
              path: {
                analysis_id: target.analysisId,
                document_id: target.documentId,
                analysis_field_id: target.analysisFieldId,
              },
              query: { machine_revision_number: target.machineRevisionNumber },
            },
            body: input,
          },
        ),
      );
    },
    async retractAnalysisCellFeedback(target) {
      await unwrapVoid(
        api.DELETE(
          '/api/v1/analyses/{analysis_id}/cells/{document_id}/{analysis_field_id}/feedback',
          {
            params: {
              path: {
                analysis_id: target.analysisId,
                document_id: target.documentId,
                analysis_field_id: target.analysisFieldId,
              },
              query: { machine_revision_number: target.machineRevisionNumber },
            },
          },
        ),
      );
    },
    async getAnalysisFieldReviewSummary(analysisId, analysisFieldId, options) {
      return unwrapJson(
        api.GET(
          '/api/v1/analyses/{analysis_id}/fields/{analysis_field_id}/review-summary',
          {
            params: {
              path: {
                analysis_id: analysisId,
                analysis_field_id: analysisFieldId,
              },
            },
            ...(options?.signal === undefined ? {} : { signal: options.signal }),
          },
        ),
      );
    },
    async listAnalysisReviewQueue(analysisId, options) {
      const query: paths['/api/v1/analyses/{analysis_id}/review-queue']['get']['parameters']['query'] = {};
      if (options?.tiers !== undefined) query.tier = [...options.tiers];
      const documentFilter = options?.documentFilter;
      if (documentFilter !== undefined) {
        if (documentFilter.q != null) query.q = documentFilter.q;
        query.q_mode = documentFilter.q_mode;
        if (documentFilter.tags.length > 0) query.tags = [...documentFilter.tags];
        query.tag_match = documentFilter.tag_match;
        if (documentFilter.folder_paths.length > 0) query.folder_path = [...documentFilter.folder_paths];
      }
      if (options?.limit !== undefined) query.limit = options.limit;
      if (options?.cursor !== undefined) query.cursor = options.cursor;
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/review-queue', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: {
            path: { analysis_id: analysisId },
            query,
          },
        }),
      );
    },
    async writeCellOverride(input) {
      const body: WriteCellOverrideRequest = {
        value: input.value,
        base_effective_version: input.baseEffectiveVersion,
      };
      if (input.displayValue !== undefined) body.display_value = input.displayValue;
      if (input.note !== undefined) body.note = input.note;
      if (input.auditGroupId !== undefined) body.audit_group_id = input.auditGroupId;
      return withConflictResult(
        unwrapJson(
          api.PATCH('/api/v1/analyses/{analysis_id}/cells/{document_id}/{analysis_field_id}', {
            params: {
              path: {
                analysis_id: input.analysisId,
                document_id: input.documentId,
                analysis_field_id: input.analysisFieldId,
              },
            },
            body,
            ...(input.signal === undefined ? {} : { signal: input.signal }),
          }),
        ),
      );
    },
    async acceptCell(input) {
      const body: CellReviewRequest = {
        base_effective_version: input.baseEffectiveVersion,
      };
      if (input.auditGroupId !== undefined) body.audit_group_id = input.auditGroupId;
      return withConflictResult(
        unwrapJson(
          api.POST(
            '/api/v1/analyses/{analysis_id}/cells/{document_id}/{analysis_field_id}/accept',
            {
              params: {
                path: {
                  analysis_id: input.analysisId,
                  document_id: input.documentId,
                  analysis_field_id: input.analysisFieldId,
                },
              },
              body,
              ...(input.signal === undefined ? {} : { signal: input.signal }),
            },
          ),
        ),
      );
    },
    async revertCellAcceptance(input) {
      const body: CellReviewRequest = {
        base_effective_version: input.baseEffectiveVersion,
      };
      if (input.auditGroupId !== undefined) body.audit_group_id = input.auditGroupId;
      return withConflictResult(
        unwrapJson(
          api.POST(
            '/api/v1/analyses/{analysis_id}/cells/{document_id}/{analysis_field_id}/revert-accept',
            {
              params: {
                path: {
                  analysis_id: input.analysisId,
                  document_id: input.documentId,
                  analysis_field_id: input.analysisFieldId,
                },
              },
              body,
              ...(input.signal === undefined ? {} : { signal: input.signal }),
            },
          ),
        ),
      );
    },
    async clearCellOverride(input) {
      const body: CellReviewRequest = {
        base_effective_version: input.baseEffectiveVersion,
      };
      if (input.auditGroupId !== undefined) body.audit_group_id = input.auditGroupId;
      return withConflictResult(
        unwrapJson(
          api.POST(
            '/api/v1/analyses/{analysis_id}/cells/{document_id}/{analysis_field_id}/clear',
            {
              params: {
                path: {
                  analysis_id: input.analysisId,
                  document_id: input.documentId,
                  analysis_field_id: input.analysisFieldId,
                },
              },
              body,
              ...(input.signal === undefined ? {} : { signal: input.signal }),
            },
          ),
        ),
      );
    },
    async applyBulkCellMutations(input) {
      const body = toBulkCellMutationsRequest(input);
      const bodyBytes = bulkCellMutationsBodyBytes(body);
      if (bodyBytes > REQUEST_BODY_MAX_BYTES) {
        throw buildClientValidationError(BULK_CELL_MUTATIONS_TOO_LARGE_MESSAGE, {
          code: BULK_CELL_MUTATIONS_TOO_LARGE_CODE,
          details: { bodyBytes, maxBodyBytes: REQUEST_BODY_MAX_BYTES },
        });
      }
      return unwrapJson(
        api.POST('/api/v1/analyses/{analysis_id}/cells:bulk', {
          params: { path: { analysis_id: input.analysisId } },
          body,
          ...(input.signal === undefined ? {} : { signal: input.signal }),
        }),
      );
    },
    async getAnalysisRevision(analysisId, revisionNumber) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/revisions/{revision_number}', {
          params: { path: { analysis_id: analysisId, revision_number: revisionNumber } },
        }),
      );
    },
    async getAuthSession() {
      return unwrapJson(api.GET('/api/v1/auth/session', {}));
    },
    async deleteGroundTruthSample(sampleId) {
      await unwrapVoid(
        api.POST('/api/v1/ground-truth/{sample_id}', {
          params: { path: { sample_id: sampleId } },
        }),
      );
    },
    async deleteFolioConversation(conversationId) {
      await unwrapVoid(
        api.POST('/api/v1/folio/conversations/{conversation_id}', {
          params: { path: { conversation_id: conversationId } },
        }),
      );
    },
    async deactivateTenantChargeCode(tenantId, chargeCodeId) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/charge-codes/{charge_code_id}', {
          params: { path: { tenant_id: tenantId, charge_code_id: chargeCodeId } },
        }),
      );
    },
    async executeFolioAction(input) {
      return unwrapJson(api.POST('/api/v1/folio/actions/execute', { body: input }));
    },
    async folioChat(input, chatOptions = {}) {
      const init: RequestInit = {
        body: JSON.stringify(input),
        headers: {
          'content-type': 'application/json',
          accept: 'text/event-stream',
        },
        method: 'POST',
      };
      if (chatOptions.signal) {
        init.signal = chatOptions.signal;
      }
      return requestResponse('/folio/chat', init);
    },
    async getDashboardActivity(tenantId, requestOptions = {}) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/dashboard/activity', {
          ...(requestOptions.signal === undefined ? {} : { signal: requestOptions.signal }),
          params: {
            path: { tenant_id: tenantId },
            query: {
              ...(requestOptions.since == null ? {} : { since: requestOptions.since }),
              ...(requestOptions.projectId === undefined ? {} : { project_id: requestOptions.projectId }),
            },
          },
        }),
      );
    },
    async listDashboardProjects(tenantId, requestOptions = {}) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/dashboard/projects', {
          ...(requestOptions.signal === undefined ? {} : { signal: requestOptions.signal }),
          params: {
            path: { tenant_id: tenantId },
            query: requestOptions.projectId === undefined ? {} : { project_id: requestOptions.projectId },
          },
        }),
      );
    },
    async getBatch(batchId, requestOptions = {}) {
      return unwrapJson(
        api.GET('/api/v1/batches/{batch_id}', {
          params: {
            path: { batch_id: batchId },
            query: requestOptions.includeEvents === undefined
              ? {}
              : { include_events: requestOptions.includeEvents },
          },
        }),
      );
    },
    async getBatchProcessingLog(batchId, requestOptions = {}) {
      const query = buildQueryString({
        page: String(requestOptions.page ?? 1),
        page_size: String(requestOptions.pageSize ?? 50),
      });
      const response = await requestResponse(
        `/batches/${encodePathSegment(batchId)}/processing-log?${query}`,
        { method: 'GET' },
      );
      const payload = await parseResponseBody(response);
      const normalizedPayload = normalizeBatchProcessingLogPageResponse(payload);
      if (normalizedPayload === null) {
        throw buildClientValidationError(
          'The batch processing log response did not match the expected shape.',
          {
            code: 'invalid-batch-processing-log-response',
            details: payload,
          },
        );
      }
      return normalizedPayload;
    },
    async getBillingPeriodDetail(tenantId, periodMonth) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/billing/periods/{period_month}', {
          params: {
            path: {
              tenant_id: tenantId,
              period_month: periodMonth,
            },
          },
        }),
      );
    },
    async getBillingSummary(tenantId) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/billing/summary', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async getBillingWorkspaceDirectorySummary(tenantId) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/billing/workspace-summary', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async getBillingWorkspaceCostBreakdown(tenantId) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/billing/workspace-breakdown', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async reconcileUnresolvedProviderCosts(tenantId, input) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/billing/provider-costs/reconcile', {
          params: { path: { tenant_id: tenantId } },
          body: input,
        }),
      );
    },
    async listUnresolvedProviderCosts(tenantId, options = {}) {
      const query = {
        ...(options.cursor === undefined ? {} : { cursor: options.cursor }),
        ...(options.limit === undefined ? {} : { limit: options.limit }),
      };
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/billing/provider-costs/unresolved', {
          params: {
            path: { tenant_id: tenantId },
            query,
          },
        }),
      );
    },
    async listProviderCostCorrections(tenantId, options = {}) {
      const query = {
        ...(options.cursor === undefined ? {} : { cursor: options.cursor }),
        ...(options.limit === undefined ? {} : { limit: options.limit }),
      };
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/billing/provider-costs/corrections', {
          params: {
            path: { tenant_id: tenantId },
            query,
          },
        }),
      );
    },
    async getMyAccessRequestStatus(token) {
      const normalizedToken = normalizeIdentityToken(token);
      return unwrapJson(
        api.GET('/api/v1/access-requests/mine', {
          headers: authorizationHeader(normalizedToken),
        }),
      );
    },
    async getHealth() {
      return unwrapJson(api.GET('/api/v1/health', {}));
    },
    async getCurrentUser() {
      return unwrapJson(api.GET('/api/v1/users/me', {}));
    },
    async getCurrentUserProjectAccess() {
      return unwrapJson(api.GET('/api/v1/users/me/projects', {}));
    },
    async getDocument(documentId) {
      return unwrapJson(
        api.GET('/api/v1/documents/{document_id}', {
          params: { path: { document_id: documentId } },
        }),
      );
    },
    async getGroundTruthOutcomeSuggestions(templateVersionId) {
      return unwrapJson(
        api.GET('/api/v1/ground-truth/outcome-suggestions', {
          params: { query: { template_version_id: templateVersionId } },
        }),
      );
    },
    async downloadDocument(documentId) {
      return requestResponse(`/documents/${encodePathSegment(documentId)}/download`, {
        headers: {
          accept: 'application/octet-stream',
        },
        method: 'GET',
      });
    },
    async getFolioMessageFeedback(messageId) {
      return unwrapJson(
        api.GET('/api/v1/folio/messages/{message_id}/feedback', {
          params: { path: { message_id: messageId } },
        }),
      );
    },
    async downloadTemplateWorkbook(templateType) {
      const query = buildQueryString({ template_type: templateType });
      const path = '/templates/workbook?' + query;
      return requestResponse(path, {
        headers: {
          accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        method: 'GET',
      });
    },
    async downloadExistingTemplateWorkbook(templateId) {
      const path = '/templates/' + encodePathSegment(templateId) + '/workbook';
      return requestResponse(path, {
        headers: {
          accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        method: 'GET',
      });
    },
    async exportBillingInvoice(tenantId, periodMonth) {
      return requestResponse(
        `/tenants/${encodePathSegment(tenantId)}/billing/periods/${encodePathSegment(periodMonth)}/invoice`,
        {
          headers: {
            accept: 'application/pdf',
          },
          method: 'GET',
        },
      );
    },
    async exportCumulativeBillingInvoice(tenantId) {
      return requestResponse(
        `/tenants/${encodePathSegment(tenantId)}/billing/invoice/cumulative`,
        {
          headers: {
            accept: 'application/pdf',
          },
          method: 'GET',
        },
      );
    },
    async createAnalysisExport(analysisId, input, exportOptions) {
      if (input.document_ids?.length === 0) {
        throw buildClientValidationError('Choose at least one document to export.', {
          code: 'empty-export-scope',
          details: { field: 'document_ids' },
        });
      }
      const idempotencyKey = exportOptions?.idempotencyKey;
      return unwrapExportObservation(
        api.POST('/api/v1/analyses/{analysis_id}/exports/create', {
          params: {
            path: { analysis_id: analysisId },
            header: idempotencyKey === undefined ? {} : { 'Idempotency-Key': idempotencyKey },
          },
          body: input,
        }),
      );
    },
    async listAnalysisExports(analysisId) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/exports', {
          params: { path: { analysis_id: analysisId } },
        }),
      );
    },
    async getAnalysisExport(analysisId, exportId) {
      return unwrapExportObservation(
        api.GET('/api/v1/analyses/{analysis_id}/exports/{export_id}', {
          params: { path: { analysis_id: analysisId, export_id: exportId } },
        }),
      );
    },
    async cancelAnalysisExport(analysisId, exportId) {
      return unwrapJson(
        api.POST('/api/v1/analyses/{analysis_id}/exports/{export_id}/cancel', {
          params: { path: { analysis_id: analysisId, export_id: exportId } },
        }),
      );
    },
    async downloadAnalysisExport(analysisId, exportId) {
      const result = await api.GET('/api/v1/analyses/{analysis_id}/exports/{export_id}/file', {
        params: { path: { analysis_id: analysisId, export_id: exportId } },
        parseAs: 'blob',
      });
      return new Response(await unwrapJson(Promise.resolve(result)), { headers: result.response.headers });
    },
    async exportTsaGenerationRun(generationRunId, exportFormat) {
      const normalizedExportFormat = exportFormat === 'json' ? 'xlsx' : exportFormat;
      const query =
        normalizedExportFormat === undefined
          ? ''
          : `?export_format=${normalizedExportFormat}`;
      const acceptHeader =
        normalizedExportFormat === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/zip';
      return requestResponse(
        `/tsa/runs/${encodePathSegment(generationRunId)}/export${query}`,
        {
          headers: {
            accept: acceptHeader,
          },
          method: 'GET',
        },
      );
    },
    async exportTsaIdentifiedService(generationRunId, identifiedServiceId) {
      return requestResponse(
        `/tsa/runs/${encodePathSegment(generationRunId)}/services/${encodePathSegment(identifiedServiceId)}/export`,
        {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          },
          method: 'GET',
        },
      );
    },
    async getProject(projectId) {
      return unwrapJson(
        api.GET('/api/v1/projects/{project_id}', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async getProjectPermissions(projectId) {
      return unwrapJson(
        api.GET('/api/v1/projects/{project_id}/permissions', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async getProjectPresence(projectId) {
      return unwrapJson(
        api.GET('/api/v1/projects/{project_id}/presence', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async getProjectTrash(projectId) {
      return unwrapJson(
        api.GET('/api/v1/projects/{project_id}/trash', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async getProjectUsage(projectId, options) {
      return unwrapJson(
        api.GET('/api/v1/projects/{project_id}/usage', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async getProjectDocumentIngestSummary(projectId, options) {
      return unwrapJson(
        api.GET('/api/v1/projects/{project_id}/documents/ingest-summary', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async getEvaluationRun(evalRunId, options) {
      return unwrapJson(
        api.GET('/api/v1/evaluations/{eval_run_id}', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: { path: { eval_run_id: evalRunId } },
        }),
      );
    },
    async getFolioConversation(conversationId) {
      return unwrapJson(
        api.GET('/api/v1/folio/conversations/{conversation_id}', {
          params: { path: { conversation_id: conversationId } },
        }),
      );
    },
    async getFolioGuidance(projectId) {
      return unwrapJson(
        api.GET('/api/v1/folio/guidance', {
          params: { query: projectId === undefined ? {} : { project_id: projectId } },
        }),
      );
    },
    async upsertFolioMessageFeedback(messageId, input) {
      return unwrapJson(
        api.PUT('/api/v1/folio/messages/{message_id}/feedback', {
          params: { path: { message_id: messageId } },
          body: input,
        }),
      );
    },
    async retractFolioMessageFeedback(messageId) {
      await unwrapVoid(
        api.DELETE('/api/v1/folio/messages/{message_id}/feedback', {
          params: { path: { message_id: messageId } },
        }),
      );
    },
    async getLatestEvaluationRunForRun(runId) {
      return unwrapJson(
        api.GET('/api/v1/evaluations/by-run/{run_id}', {
          params: { path: { run_id: runId } },
        }),
      );
    },
    async getLatestTsaGenerationStatus(projectId, templateVersionId) {
      return unwrapJson(
        api.GET('/api/v1/tsa/runs/latest', {
          params: {
            query: { project_id: projectId, template_version_id: templateVersionId },
          },
        }),
      );
    },
    getTsaGenerationRunEventsUrl(generationRunId) {
      return resolveRequestUrl(
        baseUrl,
        '/tsa/runs/' + encodePathSegment(generationRunId) + '/events',
      );
    },
    async getLatestTsaGenerationRun(projectId, templateVersionId) {
      return this.getTsaGenerationRun(await resolveLatestTsaRunId(projectId, templateVersionId));
    },
    async getLatestTsaRiskAssessment(projectId, templateVersionId) {
      return this.getTsaRiskAssessment(await resolveLatestTsaRunId(projectId, templateVersionId));
    },
    async getLatestTsaFactSheet(projectId, templateVersionId) {
      return this.getTsaFactSheet(await resolveLatestTsaRunId(projectId, templateVersionId));
    },
    async updateTsaFactSheetField(generationRunId, input) {
      return unwrapJson(
        api.PATCH('/api/v1/tsa/runs/{generation_run_id}/fact-sheet', {
          body: input,
          params: {
            path: {
              generation_run_id: generationRunId,
            },
          },
        }),
      );
    },
    async getLatestTsaFindings(projectId, templateVersionId) {
      return this.getTsaFindings(await resolveLatestTsaRunId(projectId, templateVersionId));
    },
    async getLatestTsaCosts(projectId, templateVersionId) {
      return this.getTsaCosts(await resolveLatestTsaRunId(projectId, templateVersionId));
    },
    async regenerateLatestTsaCapabilityRuns(projectId, templateVersionId) {
      return unwrapJson(
        api.POST('/api/v1/tsa/runs/latest/capabilities/regenerate', {
          params: {
            query: { project_id: projectId, template_version_id: templateVersionId },
          },
        }),
      );
    },
    async listTsaIdentifiedServices(generationRunId) {
      return unwrapJson(
        api.GET('/api/v1/tsa/runs/{generation_run_id}/services', {
          params: { path: { generation_run_id: generationRunId } },
        }),
      );
    },
    async updateTsaIdentifiedServiceField(generationRunId, identifiedServiceId, input) {
      return unwrapJson(
        api.PATCH('/api/v1/tsa/runs/{generation_run_id}/services/{identified_service_id}', {
          body: input,
          params: {
            path: {
              generation_run_id: generationRunId,
              identified_service_id: identifiedServiceId,
            },
          },
        }),
      );
    },
    async addTsaColumn(generationRunId, input) {
      return unwrapJson(
        api.POST('/api/v1/tsa/runs/{generation_run_id}/columns', {
          body: input,
          params: {
            path: {
              generation_run_id: generationRunId,
            },
          },
        }),
      );
    },
    async addTsaService(generationRunId, input) {
      return unwrapJson(
        api.POST('/api/v1/tsa/runs/{generation_run_id}/services', {
          body: {
            ...input,
            force_add: input.force_add ?? false,
          },
          params: {
            path: {
              generation_run_id: generationRunId,
            },
          },
        }),
      );
    },
    async deleteTsaIdentifiedService(generationRunId, identifiedServiceId) {
      await unwrapVoid(
        api.DELETE('/api/v1/tsa/runs/{generation_run_id}/services/{identified_service_id}', {
          params: {
            path: {
              generation_run_id: generationRunId,
              identified_service_id: identifiedServiceId,
            },
          },
        }),
      );
    },
    async getTsaRiskAssessment(generationRunId) {
      return unwrapJson(
        api.GET('/api/v1/tsa/runs/{generation_run_id}/risk', {
          params: { path: { generation_run_id: generationRunId } },
        }),
      );
    },
    async getTsaFactSheet(generationRunId) {
      return unwrapJson(
        api.GET('/api/v1/tsa/runs/{generation_run_id}/fact-sheet', {
          params: { path: { generation_run_id: generationRunId } },
        }),
      );
    },
    async getTsaFindings(generationRunId) {
      return unwrapJson(
        api.GET('/api/v1/tsa/runs/{generation_run_id}/findings', {
          params: { path: { generation_run_id: generationRunId } },
        }),
      );
    },
    async getTsaCosts(generationRunId) {
      return unwrapJson(
        api.GET('/api/v1/tsa/runs/{generation_run_id}/costs', {
          params: { path: { generation_run_id: generationRunId } },
        }),
      );
    },
    async listTsaCapabilityRuns(generationRunId) {
      return unwrapJson(
        api.GET('/api/v1/tsa/runs/{generation_run_id}/capabilities', {
          params: { path: { generation_run_id: generationRunId } },
        }),
      );
    },
    async getRun(runId) {
      return unwrapJson(
        api.GET('/api/v1/runs/{run_id}', {
          params: { path: { run_id: runId } },
        }),
      );
    },
    async getRunResultDetail(runId, resultId) {
      return unwrapJson(
        api.GET('/api/v1/runs/{run_id}/results/{result_id}', {
          params: { path: { run_id: runId, result_id: resultId } },
        }),
      );
    },
    async getTsaGenerationRun(generationRunId) {
      const detail = await unwrapJson(
        api.GET('/api/v1/tsa/runs/{generation_run_id}', {
          params: { path: { generation_run_id: generationRunId } },
        }),
      );

      return adaptTsaGenerationRunDetailResponse(detail);
    },
    async getTenant(tenantId) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async getWorkspacePricing(tenantId) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/pricing', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async getWorkspaceMarketplaceItems(tenantId) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/pricing/marketplace-items', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async listManualCharges(tenantId) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/manual-charges', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async createManualCharge(tenantId, input) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/manual-charges', {
          params: { path: { tenant_id: tenantId } },
          body: input,
        }),
      );
    },
    async deleteManualCharge(tenantId, manualChargeId) {
      await unwrapVoid(
        api.DELETE('/api/v1/tenants/{tenant_id}/manual-charges/{manual_charge_id}', {
          params: { path: { tenant_id: tenantId, manual_charge_id: manualChargeId } },
        }),
      );
    },
    async getTenantUsage(tenantId) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/usage', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async getTenantUsageByChargeCode(tenantId) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/usage/by-charge-code', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async getControlPlanePortfolio(query, options) {
      return unwrapJson(
        api.GET('/api/v1/control-plane/portfolio', {
          params: { query },
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
        }),
      );
    },
    async setTenantAccountKind(tenantId, input) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/set-account-kind', {
          params: { path: { tenant_id: tenantId } },
          body: input,
        }),
      );
    },
    async markDashboardVisit(tenantId) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/dashboard/visit', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async listUserApiKeys(options = {}) {
      return unwrapJson(
        api.GET('/api/v1/users/me/api-keys', {
          params: {
            query: options.includeRevoked ? { include_revoked: true } : {},
          },
        }),
      );
    },
    async createUserApiKey(input) {
      return unwrapJson(api.POST('/api/v1/users/me/api-keys', { body: input }));
    },
    async revokeUserApiKey(keyId) {
      return unwrapJson(
        api.POST('/api/v1/users/me/api-keys/{key_id}', {
          params: { path: { key_id: keyId } },
        }),
      );
    },
    async getUserApiKeyUsage(keyId) {
      return unwrapJson(
        api.GET('/api/v1/users/me/api-keys/{key_id}/usage', {
          params: { path: { key_id: keyId } },
        }),
      );
    },
    async getUserApiKeyProvisioningContext() {
      return unwrapJson(api.GET('/api/v1/users/me/api-keys/provisioning-context', {}));
    },
    async getTemplate(templateId) {
      return unwrapJson(
        api.GET('/api/v1/templates/{template_id}', {
          params: { path: { template_id: templateId } },
        }),
      );
    },
    async getTemplateVersion(templateId, versionId) {
      return unwrapJson(
        api.GET('/api/v1/templates/{template_id}/versions/{version_id}', {
          params: { path: { template_id: templateId, version_id: versionId } },
        }),
      );
    },
    async getUser(userId) {
      return unwrapJson(
        api.GET('/api/v1/users/{user_id}', {
          params: { path: { user_id: userId } },
        }),
      );
    },
    async getUserProjectAccess(userId) {
      return unwrapJson(
        api.GET('/api/v1/users/{user_id}/project-access', {
          params: { path: { user_id: userId } },
        }),
      );
    },
    async getWorkspaceCatalog() {
      return unwrapJson(api.GET('/api/v1/workspace', {}));
    },
    async getWorkspaceTrash() {
      return unwrapJson(api.GET('/api/v1/workspace/trash', {}));
    },
    async getPermissionCatalog() {
      return unwrapJson(api.GET('/api/v1/permissions/catalog', {}));
    },
    async inviteTenantUser(tenantId, input) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/invite', {
          params: { path: { tenant_id: tenantId } },
          body: input,
        }),
      );
    },
    async listMyWorkspaceInvitations() {
      return unwrapJson(api.GET('/api/v1/users/me/invitations', {}));
    },
    async acceptMyWorkspaceInvitation(invitationId) {
      return unwrapJson(
        api.POST('/api/v1/users/me/invitations/{invitation_id}/accept', {
          params: { path: { invitation_id: invitationId } },
        }),
      );
    },
    async declineMyWorkspaceInvitation(invitationId) {
      return unwrapJson(
        api.POST('/api/v1/users/me/invitations/{invitation_id}/decline', {
          params: { path: { invitation_id: invitationId } },
        }),
      );
    },
    async listPlatformInvitations() {
      return unwrapJson(api.GET('/api/v1/users/platform-invitations', {}));
    },
    async revokePlatformInvitation(invitationId) {
      return unwrapJson(
        api.POST('/api/v1/users/platform-invitations/{invitation_id}/revoke', {
          params: { path: { invitation_id: invitationId } },
        }),
      );
    },
    async removePlatformInvitation(invitationId) {
      await unwrapVoid(
        api.POST('/api/v1/users/platform-invitations/{invitation_id}/remove', {
          params: { path: { invitation_id: invitationId } },
        }),
      );
    },
    async acceptMyPlatformInvitation(invitationId) {
      return unwrapJson(
        api.POST('/api/v1/users/me/platform-invitations/{invitation_id}/accept', {
          params: { path: { invitation_id: invitationId } },
        }),
      );
    },
    async declineMyPlatformInvitation(invitationId) {
      return unwrapJson(
        api.POST('/api/v1/users/me/platform-invitations/{invitation_id}/decline', {
          params: { path: { invitation_id: invitationId } },
        }),
      );
    },
    async listAnalyses(projectId, options) {
      return unwrapJson(
        api.GET('/api/v1/analyses', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: {
            query: {
              project_id: projectId,
              ...(options?.sourceRunId !== undefined
                ? { source_run_id: options.sourceRunId }
                : {}),
            },
          },
        }),
      );
    },
    async listAnalysisColumns(analysisId) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/columns', {
          params: { path: { analysis_id: analysisId } },
        }),
      );
    },
    async listAnalysisRevisions(analysisId, options) {
      return unwrapJson(
        api.GET('/api/v1/analyses/{analysis_id}/revisions', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: { path: { analysis_id: analysisId } },
        }),
      );
    },
    async listBatches(projectId) {
      const response = await unwrapJson(
        api.GET('/api/v1/batches', {
          params: { query: { project_id: projectId } },
        }),
      );
      return response.items;
    },
    async listBillingCostGraph(tenantId, options) {
      const query: {
        project_id?: string | null;
        granularity?: 'day' | 'month';
        year?: number | null;
        month?: number | null;
      } = {};
      if (options?.projectId !== undefined) {
        query.project_id = options.projectId;
      }
      if (options?.granularity !== undefined) {
        query.granularity = options.granularity;
      }
      if (options?.year !== undefined) {
        query.year = options.year;
      }
      if (options?.month !== undefined) {
        query.month = options.month;
      }
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/billing/cost-graph', {
          params: {
            path: { tenant_id: tenantId },
            query,
          },
        }),
      );
    },
    async listBillingPeriods(tenantId) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/billing/periods', {
          params: { path: { tenant_id: tenantId } },
        }),
      );
    },
    async listBillingProjectTotals(tenantId, options) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/billing/projects', {
          params: {
            path: { tenant_id: tenantId },
            query: options?.year !== undefined ? { year: options.year } : {},
          },
        }),
      );
    },
    async listDocuments(projectId, listOptions = {}) {
      const query: paths['/api/v1/documents']['get']['parameters']['query'] = { project_id: projectId };
      if (listOptions.tagMatch !== undefined) query.tag_match = listOptions.tagMatch;
      if (listOptions.queryMode !== undefined) query.q_mode = listOptions.queryMode;
      if (listOptions.documentIds !== undefined) {
        query.document_ids = [...listOptions.documentIds];
      }
      if (listOptions.tags !== undefined) {
        query.tags = [...listOptions.tags];
      }
      if (listOptions.tsaEnabled !== undefined) {
        query.tsa_enabled = listOptions.tsaEnabled;
      }
      if (listOptions.tsaTags !== undefined) {
        query.tsa_tags = [...listOptions.tsaTags];
      }
      if (listOptions.folderPaths !== undefined && listOptions.folderPaths.length > 0) {
        query.folder_path = [...listOptions.folderPaths];
      } else if (listOptions.folderPath !== undefined) {
        query.folder_path = [listOptions.folderPath];
      }
      if (listOptions.q !== undefined && listOptions.q.length > 0) {
        query.q = listOptions.q;
      }
      if (listOptions.page !== undefined) {
        query.page = listOptions.page;
      }
      if (listOptions.pageSize !== undefined) {
        query.page_size = listOptions.pageSize;
      }
      return unwrapJson(api.GET('/api/v1/documents', {
        params: { query },
        ...(listOptions.signal === undefined ? {} : { signal: listOptions.signal }),
      }));
    },
    async resolveDocumentUploadOutcomes(projectId, documentIds) {
      return unwrapJson(
        api.GET('/api/v1/documents/upload-outcomes', {
          params: {
            query: {
              project_id: projectId,
              document_ids: [...documentIds],
            },
          },
        }),
      );
    },
    async listDocumentTagOptions(projectId, options) {
      const folderPath =
        options?.folderPaths !== undefined && options.folderPaths.length > 0
          ? [...options.folderPaths]
          : options?.folderPath === undefined ? undefined : [options.folderPath];
      return unwrapJson(
        api.GET('/api/v1/documents/tag-options', {
          params: {
            query: {
              project_id: projectId,
              ...(folderPath === undefined ? {} : { folder_path: folderPath }),
            },
          },
        }),
      );
    },
    async listDocumentFolders(projectId) {
      return unwrapJson(
        api.GET('/api/v1/documents/folders', {
          params: { query: { project_id: projectId } },
        }),
      );
    },
    async createDocumentFolder(input) {
      return unwrapJson(
        api.POST('/api/v1/documents/folders', { body: input }),
      );
    },
    async renameDocumentFolder(input) {
      return unwrapJson(
        api.POST('/api/v1/documents/folders/rename', { body: input }),
      );
    },
    async updateDocumentFolder(documentId, input) {
      return unwrapJson(
        api.POST('/api/v1/documents/{document_id}/move', {
          params: { path: { document_id: documentId } },
          body: input,
        }),
      );
    },
    async bulkMoveDocuments(input) {
      return unwrapJson(
        api.POST('/api/v1/documents/bulk-move', { body: input }),
      );
    },
    async retryDocumentEmbedding(documentId) {
      return unwrapJson(
        api.POST('/api/v1/documents/{document_id}/retry-embedding', {
          params: { path: { document_id: documentId } },
        }),
      );
    },
    async listEvaluationRuns(projectId, options) {
      return unwrapJson(
        api.GET('/api/v1/evaluations', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: { query: { project_id: projectId } },
        }),
      );
    },
    async trashEvaluationRun(evalRunId) {
      await unwrapVoid(
        api.POST('/api/v1/evaluations/{eval_run_id}/trash', {
          params: { path: { eval_run_id: evalRunId } },
        }),
      );
    },
    async restoreEvaluationRun(evalRunId) {
      await unwrapVoid(
        api.POST('/api/v1/evaluations/{eval_run_id}/restore', {
          params: { path: { eval_run_id: evalRunId } },
        }),
      );
    },
    async listFolioConversations() {
      return unwrapJson(api.GET('/api/v1/folio/conversations', {}));
    },
    async listPendingJoinRequests() {
      return unwrapJson(api.GET('/api/v1/access-requests/pending-joins', {}));
    },
    async listPendingNewTenantRequests() {
      return unwrapJson(api.GET('/api/v1/access-requests/pending-tenants', {}));
    },
    async listBulkProjectPresence(projectIds) {
      if (projectIds.length === 0) {
        return { projects: {} };
      }
      return unwrapJson(
        api.GET('/api/v1/presence/bulk', {
          params: { query: { project_ids: projectIds.join(',') } },
        }),
      );
    },
    async listProjectAutomations(projectId) {
      return unwrapJson(
        api.GET('/api/v1/projects/{project_id}/automations', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async createProjectAutomation(projectId, input) {
      return unwrapJson(
        api.POST('/api/v1/projects/{project_id}/automations', {
          params: { path: { project_id: projectId } },
          body: input,
        }),
      );
    },
    async updateProjectAutomation(projectId, ruleId, input) {
      return unwrapJson(
        api.PATCH('/api/v1/projects/{project_id}/automations/{rule_id}', {
          params: { path: { project_id: projectId, rule_id: ruleId } },
          body: input,
        }),
      );
    },
    async deleteProjectAutomation(projectId, ruleId) {
      await unwrapVoid(
        api.DELETE('/api/v1/projects/{project_id}/automations/{rule_id}', {
          params: { path: { project_id: projectId, rule_id: ruleId } },
        }),
      );
    },
    async enableProjectAutomation(projectId, ruleId) {
      return unwrapJson(
        api.POST('/api/v1/projects/{project_id}/automations/{rule_id}/enable', {
          params: { path: { project_id: projectId, rule_id: ruleId } },
        }),
      );
    },
    async disableProjectAutomation(projectId, ruleId) {
      return unwrapJson(
        api.POST('/api/v1/projects/{project_id}/automations/{rule_id}/disable', {
          params: { path: { project_id: projectId, rule_id: ruleId } },
        }),
      );
    },
    async listProjectMembers(projectId) {
      return unwrapJson(
        api.GET('/api/v1/projects/{project_id}/members', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async listAssignableProjectUsers(projectId) {
      return unwrapJson(
        api.GET('/api/v1/projects/{project_id}/members/assignable', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async listProjectTemplates(projectId) {
      const response = await unwrapJson(
        api.GET('/api/v1/projects/{project_id}/templates', {
          params: { path: { project_id: projectId } },
        }),
      );
      return response.items;
    },
    async listProjects() {
      const response = await unwrapJson(api.GET('/api/v1/projects', {}));
      return response.items;
    },
    async listGroundTruthSamples(projectId, listOptions = {}) {
      const query: {
        project_id: string;
        document_id?: string;
        template_version_id?: string;
      } = { project_id: projectId };
      if (listOptions.documentId !== undefined) {
        query.document_id = listOptions.documentId;
      }
      if (listOptions.templateVersionId !== undefined) {
        query.template_version_id = listOptions.templateVersionId;
      }
      return unwrapJson(api.GET('/api/v1/ground-truth', { params: { query } }));
    },
    async listRunResults(runId) {
      return unwrapJson(
        api.GET('/api/v1/runs/{run_id}/results', {
          params: { path: { run_id: runId } },
        }),
      );
    },
    async listRuns(projectId, options) {
      return unwrapJson(
        api.GET('/api/v1/runs', {
          ...(options?.signal === undefined ? {} : { signal: options.signal }),
          params: { query: projectId === undefined ? {} : { project_id: projectId } },
        }),
      );
    },
    async listSuperadmins() {
      return unwrapJson(api.GET('/api/v1/users/superadmins', {}));
    },
    async listTenants() {
      return unwrapJson(api.GET('/api/v1/tenants', {}));
    },
    async listTenantInvitations(tenantId, options) {
      const params =
        options?.statuses === undefined
          ? { path: { tenant_id: tenantId } }
          : {
              path: { tenant_id: tenantId },
              query: { statuses: [...options.statuses] },
            };
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/invitations', {
          params,
        }),
      );
    },
    async listTemplates() {
      const response = await unwrapJson(api.GET('/api/v1/templates', {}));
      return response.items;
    },
    async listTsaGenerationRuns(projectId, templateVersionId) {
      return unwrapJson(
        api.GET('/api/v1/tsa/runs', {
          params: {
            query: { project_id: projectId, template_version_id: templateVersionId },
          },
        }),
      );
    },
    async listUsers() {
      return unwrapJson(api.GET('/api/v1/users', {}));
    },
    async onboardTenant(input) {
      return unwrapJson(api.POST('/api/v1/tenants/onboard', { body: input }));
    },
    async previewDocument(documentId) {
      return unwrapJson(
        api.GET('/api/v1/documents/{document_id}/preview', {
          params: { path: { document_id: documentId } },
        }),
      );
    },
    async rejectAccessRequest(requestId, input) {
      return unwrapJson(
        api.POST('/api/v1/access-requests/{request_id}/reject', {
          params: { path: { request_id: requestId } },
          body: input,
        }),
      );
    },
    async rejectTenantChargeCode(tenantId, chargeCodeId, input) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/charge-codes/{charge_code_id}/reject', {
          params: { path: { tenant_id: tenantId, charge_code_id: chargeCodeId } },
          body: input,
        }),
      );
    },
    async restoreTrashedDocument(documentId) {
      return unwrapJson(
        api.POST('/api/v1/workspace/trash/documents/{document_id}/restore', {
          params: { path: { document_id: documentId } },
        }),
      );
    },
    async restoreTrashedProject(projectId) {
      return unwrapJson(
        api.POST('/api/v1/workspace/trash/projects/{project_id}/restore', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async restoreTrashedTemplate(templateId) {
      return unwrapJson(
        api.POST('/api/v1/workspace/trash/templates/{template_id}/restore', {
          params: { path: { template_id: templateId } },
        }),
      );
    },
    async resendTenantInvitation(tenantId, invitationId) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/invitations/{invitation_id}/resend', {
          params: { path: { tenant_id: tenantId, invitation_id: invitationId } },
        }),
      );
    },
    async revokeTenantInvitation(tenantId, invitationId) {
      return unwrapJson(
        api.POST('/api/v1/tenants/{tenant_id}/invitations/{invitation_id}/revoke', {
          params: { path: { tenant_id: tenantId, invitation_id: invitationId } },
        }),
      );
    },
    async removeTenantInvitation(tenantId, invitationId) {
      await unwrapVoid(
        api.POST('/api/v1/tenants/{tenant_id}/invitations/{invitation_id}/remove', {
          params: { path: { tenant_id: tenantId, invitation_id: invitationId } },
        }),
      );
    },
    async removeProjectMember(projectId, userId) {
      await unwrapVoid(
        api.POST('/api/v1/projects/{project_id}/members/{user_id}/remove', {
          params: { path: { project_id: projectId, user_id: userId } },
        }),
      );
    },
    async assignTemplateToProject(projectId, templateId, input) {
      return unwrapJson(
        api.POST('/api/v1/projects/{project_id}/templates/{template_id}/assign', {
          params: { path: { project_id: projectId, template_id: templateId } },
          body: input ?? null,
        }),
      );
    },
    async signOut() {
      await unwrapVoid(api.POST('/api/v1/auth/signout', {}));
    },
    async submitJoinTenantRequest(token, input) {
      const normalizedToken = normalizeIdentityToken(token);
      return unwrapJson(
        api.POST('/api/v1/access-requests/join', {
          headers: authorizationHeader(normalizedToken),
          body: input,
        }),
      );
    },
    async submitNewTenantRequest(token, input) {
      const normalizedToken = normalizeIdentityToken(token);
      return unwrapJson(
        api.POST('/api/v1/access-requests/new-tenant', {
          headers: authorizationHeader(normalizedToken),
          body: input,
        }),
      );
    },
    async suggestTenantChargeCodes(tenantId, limit) {
      return unwrapJson(
        api.GET('/api/v1/tenants/{tenant_id}/charge-codes/suggestions', {
          params: {
            path: { tenant_id: tenantId },
            query: limit === undefined ? {} : { limit },
          },
        }),
      );
    },
    async startEvaluationRun(input) {
      return unwrapJson(api.POST('/api/v1/evaluations/run', { body: input }));
    },
    async startTsaGeneration(input) {
      return unwrapJson(api.POST('/api/v1/tsa/runs', { body: input }));
    },
    async regenerateTsaGenerationRun(generationRunId, input) {
      return unwrapJson(
        api.POST('/api/v1/tsa/runs/{generation_run_id}/regenerate', {
          params: { path: { generation_run_id: generationRunId } },
          body: input,
        }),
      );
    },
    async submitBatch(input) {
      const body = buildSubmitBatchRequest(input);
      const deadline = { signal: input.signal, timeoutMs: input.timeoutMs ?? DEFAULT_BATCH_SUBMISSION_TIMEOUT_MS };
      return withRequestDeadline('/batches', deadline, signal =>
        unwrapJson(
          api.POST('/api/v1/batches', {
            body,
            params: {
              header:
                input.idempotencyKey === undefined ? {} : { 'Idempotency-Key': input.idempotencyKey },
            },
            ...(signal === undefined
              ? {}
              : { fetch: (request: Request) => sendRequestWithSignal(request, signal) }),
          }),
        ),
      );
    },
    async streamDashboardCatchUp(tenantId, streamOptions = {}) {
      const query =
        streamOptions.since == null
          ? ''
          : `?${buildQueryString({ since: streamOptions.since })}`;
      const init: RequestInit = {
        headers: {
          accept: 'text/event-stream',
        },
        method: 'GET',
      };
      if (streamOptions.signal) {
        init.signal = streamOptions.signal;
      }
      return requestResponse(
        `/tenants/${encodePathSegment(tenantId)}/dashboard/catch-up${query}`,
        init,
      );
    },
    async startDemoMode() {
      return unwrapJson(api.POST('/api/v1/auth/demo-mode/start'));
    },
    async stopDemoMode() {
      return unwrapJson(api.POST('/api/v1/auth/demo-mode/stop'));
    },
    async switchTenant(input) {
      return unwrapJson(api.POST('/api/v1/auth/switch-tenant', { body: input }));
    },
    async updateTenantChargeCode(tenantId, chargeCodeId, input) {
      return unwrapJson(
        api.PATCH('/api/v1/tenants/{tenant_id}/charge-codes/{charge_code_id}', {
          params: { path: { tenant_id: tenantId, charge_code_id: chargeCodeId } },
          body: input,
        }),
      );
    },
    async updateProject(projectId, input) {
      return unwrapJson(
        api.POST('/api/v1/projects/{project_id}/update', {
          params: { path: { project_id: projectId } },
          body: input,
        }),
      );
    },
    async updateProjectCapabilities(projectId, input) {
      return unwrapJson(
        api.PATCH('/api/v1/projects/{project_id}/capabilities', {
          params: { path: { project_id: projectId } },
          body: input,
        }),
      );
    },
    async updateProjectMemberPermissions(projectId, userId, input) {
      return unwrapJson(
        api.POST('/api/v1/projects/{project_id}/members/{user_id}/permissions', {
          params: { path: { project_id: projectId, user_id: userId } },
          body: input,
        }),
      );
    },
    async updateTenant(tenantId, input) {
      return unwrapJson(
        api.PATCH('/api/v1/tenants/{tenant_id}', {
          params: { path: { tenant_id: tenantId } },
          body: input,
        }),
      );
    },
    async setTenantTrial(tenantId, input) {
      return unwrapJson(
        api.PATCH('/api/v1/tenants/{tenant_id}/trial', {
          params: { path: { tenant_id: tenantId } },
          body: input,
        }),
      );
    },
    async updateUser(userId, input) {
      return unwrapJson(
        api.PATCH('/api/v1/users/{user_id}', {
          params: { path: { user_id: userId } },
          body: input,
        }),
      );
    },
    async updateUserPermissions(userId, input) {
      return unwrapJson(
        api.POST('/api/v1/users/{user_id}/permissions', {
          params: { path: { user_id: userId } },
          body: input,
        }),
      );
    },
    async updateDocumentTags(documentId, input) {
      return unwrapJson(
        api.POST('/api/v1/documents/{document_id}/update-tags', {
          params: { path: { document_id: documentId } },
          body: input,
        }),
      );
    },
    async upsertWorkspacePricing(tenantId, input) {
      return unwrapJson(
        api.PUT('/api/v1/tenants/{tenant_id}/pricing', {
          params: { path: { tenant_id: tenantId } },
          body: input,
        }),
      );
    },
    async createUploadBatch(projectId, input) {
      return unwrapJson(api.POST('/api/v1/projects/{project_id}/upload-batches/create', {
        params: { path: { project_id: projectId } }, body: input,
      }));
    },
    async getUploadBatch(projectId, batchId) {
      return unwrapJson(api.GET('/api/v1/projects/{project_id}/upload-batches/{batch_id}', {
        params: { path: { project_id: projectId, batch_id: batchId } },
      }));
    },
    async listUploadBatches(projectId, cursor) {
      return unwrapJson(api.GET('/api/v1/projects/{project_id}/upload-batches', {
        params: { path: { project_id: projectId }, query: { ...(cursor === undefined ? {} : { cursor }) } },
      }));
    },
    async getUploadBatchInput(projectId, batchId, inputId) {
      return unwrapJson(api.GET('/api/v1/projects/{project_id}/upload-batches/{batch_id}/inputs/{input_id}', {
        params: { path: { project_id: projectId, batch_id: batchId, input_id: inputId } },
      }));
    },
    async createUploadSession(projectId, input) {
      return unwrapJson(api.POST('/api/v1/projects/{project_id}/upload-sessions/create', {
        params: { path: { project_id: projectId } },
        body: input,
      }));
    },
    async getUploadSession(projectId, sessionId) {
      return unwrapJson(api.GET('/api/v1/projects/{project_id}/upload-sessions/{session_id}', {
        params: { path: { project_id: projectId, session_id: sessionId } },
      }));
    },
    async sendUploadSession(projectId, sessionId, part, options) {
      const last = part.offset + part.bytes.size - 1;
      return unwrapJson(api.POST('/api/v1/projects/{project_id}/upload-sessions/{session_id}/send', {
        params: {
          path: { project_id: projectId, session_id: sessionId },
          header: { 'Content-Range': `bytes ${String(part.offset)}-${String(last)}/${String(part.fileSizeBytes)}` },
        },
        headers: { 'Content-Type': OCTET_STREAM },
        body: part.bytes,
        bodySerializer: (bytes) => bytes,
        ...(options?.signal === undefined ? {} : { signal: options.signal }),
      }));
    },
    async cancelUploadSession(projectId, sessionId) {
      return unwrapJson(api.POST('/api/v1/projects/{project_id}/upload-sessions/{session_id}/cancel', {
        params: { path: { project_id: projectId, session_id: sessionId } },
      }));
    },
    async importTemplateWorkbook(input) {
      const formData = new FormData();
      formData.append('template_type', input.templateType);
      formData.append('workbook', input.workbook);
      const response = await sendRequest('/templates/workbook/import', {
        body: formData,
        method: 'POST',
      });
      const payload = await parseResponseBody(response);
      if (!response.ok) {
        throw withRetryAfter(buildRequestError(response.status, payload), response);
      }
      const normalizedPayload = normalizeTemplateWorkbookImportResponse(payload);
      if (normalizedPayload === null) {
        throw toApiClientError(response.status, payload);
      }
      return normalizedPayload;
    },
    async getCurrentReleaseNews() {
      return unwrapJson(api.GET('/api/v1/release-news/current', {}));
    },
    async acknowledgeReleaseNews(version: string) {
      return unwrapVoid(
        api.POST('/api/v1/release-news/{version}/acknowledge', {
          params: { path: { version } },
        }),
      );
    },
    async getMyAgentConnection() {
      return unwrapJson(api.GET('/api/v1/users/me/agent-connection', {}));
    },
    async dismissMyAgentConnection() {
      return unwrapJson(api.POST('/api/v1/users/me/agent-connection/dismiss', {}));
    },
    async acknowledgeMyAgentConnectionConnected() {
      return unwrapJson(api.POST('/api/v1/users/me/agent-connection/acknowledge-connected', {}));
    },
    async listExtractionRunProgress(projectId) {
      return unwrapJson(
        api.GET('/api/v1/projects/{project_id}/extraction-runs', {
          params: { path: { project_id: projectId } },
        }),
      );
    },
    async acknowledgeExtractionRun(projectId, runId) {
      return unwrapJson(
        api.POST('/api/v1/projects/{project_id}/extraction-runs/{run_id}/acknowledge', {
          params: { path: { project_id: projectId, run_id: runId } },
        }),
      );
    },
    async cancelAnalysisRevision(analysisId, revisionNumber) {
      return unwrapJson(
        api.POST('/api/v1/analyses/{analysis_id}/revisions/{revision_number}/cancel', {
          params: { path: { analysis_id: analysisId, revision_number: revisionNumber } },
        }),
      );
    },
  };
}
